import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown } from 'lucide-react';
import { Text, Icon, Select, MultiSelect, Modal } from '@ui-library';
import { Pagination } from '../components/Pagination';
import { ProjectMembersApiService, type ProjectMemberResponse } from '../services/projectMembersApi';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../components/Button/Button';
import { Input } from '../components/Input/Input';
import {
  listTickets,
  listTicketIds,
  getTicket as apiGetTicket,
  createTicket as apiCreateTicket,
  updateTicket as apiUpdateTicket,
  deleteTickets as apiDeleteTickets,
  getTicketCounts as apiGetTicketCounts,
  evaluateTickets as apiEvaluateTickets,
  checkEvaluationJob as apiCheckEvaluationJob,
  getTranslateJobStatus as apiGetTranslateJobStatus,
  checkTranslateJob as apiCheckTranslateJob,
  listEpicsGrouped as apiListEpicsGrouped,
  reorderEpicTickets as apiReorderEpicTickets,
  listEpics as apiListEpics,
  createEpic as apiCreateEpic,
  reorderEpics as apiReorderEpics,
  deleteEpic as apiDeleteEpic,
  renameEpic as apiRenameEpic,
  generateEpics as apiGenerateEpics,
  getEpicStatus as apiGetEpicStatus,
  epicCode,
  type EpicStatus,
  type EpicGroup,
  type Epic,
  type TranslateJobStatus,
  PAGE_SIZE_OPTIONS,
  type ApiAssignTo,
  type PageSize,
  type SortField as ApiSortField,
  type SortDir as ApiSortDir,
  type TicketListParams,
  type TicketEvaluationStatus,
} from '../services/ticketApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TicketStatus = 'Open' | 'In Progress' | 'Closed';
export type TicketPriority = 'Low' | 'Medium' | 'High';
export type TicketSource = 'Manual' | 'CAB';
export type TicketWorkType =
  | 'User Story'
  | 'Data Model - Reference'
  | 'Data Model - Tracked'
  | 'User Interface'
  | 'Rule'
  | 'Integration'
  | 'Workflow'
  | 'Report'
  | 'Role'
  | 'UI Policy'
  | 'Other';
export type TicketExpertise = 'Agent' | 'Human' | 'Agent + Human';
export type TicketPlatformType = 'TAP' | 'SN' | 'PA' | 'SF' | 'Web';

export interface Ticket {
  id: string;
  title: string;
  description?: string;
  note?: string;  // human-authored reviewer note from the source CAB element
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  assignTo?: ApiAssignTo | null;
  dueDate?: string;
  createdDate: string;
  createdBy: string;
  workType?: TicketWorkType;
  cabReference?: string;
  expertise?: TicketExpertise;
  platformType: TicketPlatformType;
  projectId: string;
  aiEvaluationStatus?: TicketEvaluationStatus;
  aiReasoning?: string;
  implementationSteps?: Array<{ step: string; humanRequired: boolean }>;
  estimatedEffort?: number | string;
  epicId?: string;     // id of the ticket's epic (for reassignment)
  epic?: string;       // epic name (resolved from the ticket's epic)
  buildWave?: number;  // derived from the epic's order; read-only on the ticket
}

type SortField = ApiSortField;
type SortDir = ApiSortDir;



// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: TicketStatus }> = ({ status }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {status}
  </span>
);

// ─── Inline status select (list rows) ─────────────────────────────────────────
// Compact version of the ui-library Select (sm size, same border/menu styling).
// The menu uses fixed positioning so it isn't clipped by the table's overflow
// container, and flips upward when the trigger sits too close to the bottom of
// the viewport.

const TICKET_STATUS_VALUES: TicketStatus[] = ['Open', 'In Progress', 'Closed'];

const InlineStatusSelect: React.FC<{
  status: TicketStatus;
  saving: boolean;
  onChange: (status: TicketStatus) => void;
}> = ({ status, saving, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    if (saving) return;
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuHeight = TICKET_STATUS_VALUES.length * 38 + 14;
      const openUp = rect.bottom + 4 + menuHeight > window.innerHeight;
      setMenuPos({ top: openUp ? rect.top - 4 - menuHeight : rect.bottom + 4, left: rect.left });
    }
    setIsOpen(o => !o);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const close = () => setIsOpen(false);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
        disabled={saving}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Change status (currently ${status})`}
        className={`inline-flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-xs text-bg-v2-sm font-bg-v2-sans font-bg-v2-regular text-text-primary bg-bg-primary border border-border-primary rounded-bg-v2-md transition-colors duration-200 focus:outline-none focus:border-border-brand ${saving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      >
        <span className="whitespace-nowrap">{status}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          role="listbox"
          className="fixed z-50 w-36 bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-lg border border-border-primary p-bg-v2-xs animate-fade-in-down"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {TICKET_STATUS_VALUES.map((option) => (
            <div
              key={option}
              role="option"
              aria-selected={option === status}
              className={`px-bg-v2-sm py-bg-v2-sm rounded-bg-v2-sm text-bg-v2-sm font-bg-v2-sans text-text-primary cursor-pointer transition-colors hover:bg-bg-secondary ${option === status ? 'bg-bg-secondary-subtle font-bg-v2-medium' : 'font-bg-v2-regular'}`}
              onClick={() => { setIsOpen(false); if (option !== status) onChange(option); }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─── Priority badge ───────────────────────────────────────────────────────────

const PriorityBadge: React.FC<{ priority: TicketPriority }> = ({ priority }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {priority}
  </span>
);

// ─── Plain pill (work type, source, platform) ─────────────────────────────────

const Pill: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {label}
  </span>
);

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ field: SortField; active: SortField; dir: SortDir }> = ({ field, active, dir }) => {
  const isActive = active === field;
  return (
    <span className={`inline-flex flex-col leading-none opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? '!opacity-100' : ''}`} style={{ fontSize: '9px', gap: '1px' }}>
      <span className={isActive && dir === 'asc' ? 'text-text-brand-primary' : 'text-text-quaternary'}>▲</span>
      <span className={isActive && dir === 'desc' ? 'text-text-brand-primary' : 'text-text-quaternary'}>▼</span>
    </span>
  );
};

// ─── Epic selector (ticket detail) ────────────────────────────────────────────
// Inline dropdown to change a ticket's epic, or create a new one on the fly.

const EPIC_UNASSIGNED = '__unassigned__';
const EPIC_CREATE = '__create__';

const EpicSelector: React.FC<{
  projectId: string;
  epicId?: string;
  label?: string;
  onChange: (epicId: string | null) => void;
}> = ({ projectId, epicId, label, onChange }) => {
  const [epics, setEpics] = useState<Epic[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiListEpics(projectId).then(e => { if (!cancelled) setEpics(e); }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  const createAndAssign = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const epic = await apiCreateEpic(projectId, name);
      setEpics(prev => [...prev, epic]);
      setCreating(false);
      setNewName('');
      onChange(epic.id);
    } catch (err) {
      console.error('Failed to create epic:', err);
    } finally {
      setBusy(false);
    }
  };

  if (creating) {
    return (
      <div className="flex flex-col gap-bg-v2-xs">
        {label && (
          <span className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">{label}</span>
        )}
        <div className="flex items-center gap-bg-v2-sm">
          <div className="flex-1">
            <Input
              value={newName}
              placeholder="New epic name"
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') createAndAssign();
                if (e.key === 'Escape') { setCreating(false); setNewName(''); }
              }}
            />
          </div>
          <Button label="Add" variant="primary" size="small" onClick={createAndAssign} disabled={busy || !newName.trim()} />
          <Button label="Cancel" variant="outline" size="small" onClick={() => { setCreating(false); setNewName(''); }} />
        </div>
      </div>
    );
  }

  return (
    <Select
      label={label}
      placeholder="Select epic"
      value={epicId || EPIC_UNASSIGNED}
      options={[
        ...epics.map(e => ({ value: e.id, label: e.name })),
        { value: EPIC_UNASSIGNED, label: 'Unassigned' },
        { value: EPIC_CREATE, label: '＋ Create new epic' },
      ]}
      onChange={(v) => {
        const val = String(v);
        if (val === EPIC_CREATE) { setCreating(true); return; }
        onChange(val === EPIC_UNASSIGNED ? null : val);
      }}
    />
  );
};

// ─── Ticket Detail Panel ──────────────────────────────────────────────────────

const TicketDetailPanel: React.FC<{
  ticket: Ticket;
  canEdit: boolean;
  isLoadingDetail: boolean;
  onClose: () => void;
  onSave: (updated: Ticket) => void;
  onDelete: (ticket: Ticket) => void;
  onEvaluate: (ticket: Ticket) => void;
  onCheckNow: (ticket: Ticket) => void;
  isCheckingNow?: boolean;
  assigneeOptions?: AssigneeOption[];
}> = ({ ticket, canEdit, isLoadingDetail, onClose, onSave, onDelete, onEvaluate, onCheckNow, isCheckingNow = false, assigneeOptions = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Ticket>>({});
  const [editFormError, setEditFormError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const startEdit = () => {
    setEditForm({ ...ticket });
    setEditFormError('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditFormError('');
  };

  const saveEdit = () => {
    if (!editForm.title?.trim()) { setEditFormError('Title is required.'); return; }
    const updated: Ticket = {
      ...ticket,
      title: editForm.title.trim(),
      description: editForm.description || '',
      note: editForm.note ?? '',
      status: (editForm.status as TicketStatus) || ticket.status,
      priority: (editForm.priority as TicketPriority) || ticket.priority,
      source: (editForm.source as TicketSource) || ticket.source,
      workType: (editForm.workType as TicketWorkType) || ticket.workType,
      expertise: (editForm.expertise as TicketExpertise) || ticket.expertise,
      platformType: (editForm.platformType as TicketPlatformType) || ticket.platformType,
      assignTo: editForm.assignTo ?? null,
      dueDate: editForm.dueDate || undefined,
      cabReference: editForm.cabReference || undefined,
      epicId: editForm.epicId,
    };
    onSave(updated);
    setIsEditing(false);
  };

  return (
    <>
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-bg-primary">
      {/* Header */}
      <div className="flex items-center gap-bg-v2-md h-[60px] px-bg-v2-xl border-b border-border-primary flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-bg-v2-md bg-transparent border-none cursor-pointer text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-all duration-200 flex-shrink-0"
        >
          <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
        </button>
        <Text variant="text-sm" weight="semibold" color="secondary">{ticket.id}</Text>
        <div className="ml-auto flex items-center gap-bg-v2-sm">
          {isEditing ? (
            <>
              <Button label="Cancel" variant="outline" size="small" onClick={cancelEdit} />
              <Button label="Save Changes" variant="primary" size="small" onClick={saveEdit} />
            </>
          ) : canEdit ? (
            <>
              {ticket.platformType !== 'Web' && (
                <Button
                  label={
                    ticket.aiEvaluationStatus === 'pending' ? (
                      <span className="flex items-center gap-bg-v2-xs">
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        Evaluating...
                      </span>
                    ) : (
                      <span className="flex items-center gap-bg-v2-xs">
                        <Icon name={IconName.Thought} size="sm" inheritColor />
                        Evaluate
                      </span>
                    )
                  }
                  variant="outline"
                  size="small"
                  onClick={() => onEvaluate(ticket)}
                  disabled={ticket.aiEvaluationStatus === 'pending'}
                />
              )}
              <Button
                label={
                  <span className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Edit} size="sm" inheritColor />
                    Edit
                  </span>
                }
                variant="outline"
                size="small"
                onClick={startEdit}
              />
              <Button
                label={
                  <span className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Delete} size="sm" inheritColor />
                    Delete
                  </span>
                }
                variant="outline"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                className="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto modern-scrollbar-spaced">
        {isEditing ? (
          <div className="px-bg-v2-3xl py-bg-v2-xl">
            <TicketForm
              form={editForm}
              formError={editFormError}
              onChange={(patch) => setEditForm(f => ({ ...f, ...patch }))}
              onClearError={() => setEditFormError('')}
              assigneeOptions={assigneeOptions}
              projectId={ticket.projectId}
            />
          </div>
        ) : (
          <div className="px-bg-v2-3xl py-bg-v2-xl flex flex-col gap-bg-v2-xl w-full">

            {/* Title */}
            <Text variant="text-lg" weight="semibold" color="primary">{ticket.title}</Text>

            {/* Quick status row */}
            <div className="flex items-center gap-bg-v2-md flex-wrap">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              {ticket.workType && <Pill label={ticket.workType} />}
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-4 gap-x-bg-v2-3xl gap-y-bg-v2-lg">
              <DetailField label="Status">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.status}</Text>
              </DetailField>
              <DetailField label="Priority">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.priority}</Text>
              </DetailField>
              <DetailField label="Epic">
                <Text variant="text-sm" weight="regular" color={ticket.epic ? 'primary' : 'tertiary'}>{ticket.epic || '—'}</Text>
              </DetailField>
              <DetailField label="Wave">
                <Text variant="text-sm" weight="regular" color={ticket.buildWave != null ? 'primary' : 'tertiary'}>{ticket.buildWave != null ? `Wave ${ticket.buildWave}` : '—'}</Text>
              </DetailField>
              <DetailField label="Work Type">
                <Text variant="text-sm" weight="regular" color={ticket.workType ? 'primary' : 'tertiary'}>{ticket.workType || '—'}</Text>
              </DetailField>
              <DetailField label="Source">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.source}</Text>
              </DetailField>
              <DetailField label="Expertise">
                <Text variant="text-sm" weight="regular" color={ticket.expertise ? 'primary' : 'tertiary'}>{ticket.expertise || '—'}</Text>
              </DetailField>
              <DetailField label="Estimated Effort">
                <Text variant="text-sm" weight="regular" color={ticket.estimatedEffort ? 'primary' : 'tertiary'}>{ticket.estimatedEffort ? `${ticket.estimatedEffort} Hours` : '—'}</Text>
              </DetailField>
              <DetailField label="Platform">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.platformType}</Text>
              </DetailField>
              <DetailField label="Assigned To">
                {ticket.assignTo
                  ? <Text variant="text-sm" weight="regular" color="primary">{ticket.assignTo.name}</Text>
                  : <Text variant="text-sm" weight="regular" color="tertiary">Unassigned</Text>
                }
              </DetailField>
              <DetailField label="Due Date">
                <Text variant="text-sm" weight="regular" color={ticket.dueDate ? 'primary' : 'tertiary'}>
                  {ticket.dueDate
                    ? new Date(ticket.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </Text>
              </DetailField>
              <DetailField label="Created Date">
                <Text variant="text-sm" weight="regular" color="primary">
                  {new Date(ticket.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
              </DetailField>
              <DetailField label="Created By">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.createdBy}</Text>
              </DetailField>
              {ticket.cabReference && (
                <DetailField label="CAB Reference">
                  <div className="flex items-center gap-bg-v2-xs text-text-primary">
                    <Icon name={IconName.Document} size="sm" inheritColor />
                    <Text variant="text-sm" weight="regular" color="primary">{ticket.cabReference}</Text>
                  </div>
                </DetailField>
              )}
            </div>

            {/* Pending / failed banners — above description, non-Web only */}
            {ticket.platformType !== 'Web' && !isLoadingDetail && ticket.aiEvaluationStatus === 'pending' && (
              <div className="flex items-center justify-between gap-bg-v2-md px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md bg-bg-warning-primary border border-border-warning">
                <div className="flex items-center gap-bg-v2-sm">
                  <div className="w-4 h-4 border-2 border-text-warning border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <Text variant="text-sm" weight="medium" color="primary">
                    AI Model is evaluating this ticket...
                  </Text>
                </div>
                <Button
                  label={isCheckingNow ? 'Checking...' : 'Check Now'}
                  variant="outline"
                  size="small"
                  onClick={() => onCheckNow(ticket)}
                  disabled={isCheckingNow}
                />
              </div>
            )}

            {ticket.platformType !== 'Web' && ticket.aiEvaluationStatus === 'failed' && (
              <div className="flex items-center justify-between gap-bg-v2-md px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md bg-bg-error-primary border border-border-error">
                <Text variant="text-sm" weight="medium" color="primary">
                  AI evaluation failed.
                </Text>
                {canEdit && (
                  <Button
                    label={
                      <span className="flex items-center gap-bg-v2-xs">
                        <Icon name={IconName.Thought} size="sm" inheritColor />
                        Retry
                      </span>
                    }
                    variant="outline"
                    size="small"
                    onClick={() => onEvaluate(ticket)}
                  />
                )}
              </div>
            )}

            <div className="border-t border-border-secondary" />

            {/* Description */}
            <div className="flex flex-col gap-bg-v2-sm">
              <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
                Description
              </Text>
              {isLoadingDetail ? (
                <div className="flex flex-col gap-bg-v2-sm">
                  <div className="h-3 skeleton-shimmer rounded w-full" />
                  <div className="h-3 skeleton-shimmer rounded w-4/5" />
                  <div className="h-3 skeleton-shimmer rounded w-3/5" />
                </div>
              ) : ticket.description ? (
                <div className="prose prose-sm max-w-none text-text-secondary break-words">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children, ...props }: any) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {ticket.description}
                  </ReactMarkdown>
                </div>
              ) : (
                <Text variant="text-sm" weight="regular" color="secondary">—</Text>
              )}
            </div>

            {/* Reviewer Note — human-authored commentary from the source CAB
                element (e.g. a user story). Shown whenever a note exists. */}
            {!isLoadingDetail && ticket.note && ticket.note.trim() && (
              <>
                <div className="border-t border-border-secondary" />
                <div className="flex flex-col gap-bg-v2-sm">
                  <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
                    Reviewer Note
                  </Text>
                  <div className="flex gap-bg-v2-sm px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md bg-bg-secondary border border-border-primary">
                    <Icon name={IconName.User} size="sm" className="text-text-tertiary flex-shrink-0 mt-0.5" inheritColor />
                    <Text variant="text-sm" weight="regular" color="secondary" className="whitespace-pre-wrap break-words">
                      {ticket.note}
                    </Text>
                  </div>
                </div>
              </>
            )}

            {/* AI Reasoning — below description, shown whenever data exists */}
            {ticket.platformType !== 'Web' && ticket.aiReasoning && (
              <>
                <div className="border-t border-border-secondary" />
                <div className="flex flex-col gap-bg-v2-sm">
                  <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
                    AI Recommendation
                  </Text>
                  <Text variant="text-sm" weight="regular" color="secondary" className="whitespace-pre-wrap break-words">
                    {ticket.aiReasoning}
                  </Text>
                </div>
              </>
            )}

            {/* Implementation Steps — below description, shown whenever data exists */}
            {ticket.platformType !== 'Web' && ticket.implementationSteps && ticket.implementationSteps.length > 0 && (
              <>
                <div className="border-t border-border-secondary" />
                <div className="flex flex-col gap-bg-v2-sm">
                  <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
                    Implementation Steps
                  </Text>
                  <ol className="flex flex-col gap-bg-v2-sm list-none m-0 p-0">
                    {ticket.implementationSteps.map((step, idx) => {
                      const isHuman = step.humanRequired;
                      const label = step.step;
                      return (
                        <li key={idx} className={`flex gap-bg-v2-sm px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md border ${isHuman ? 'bg-bg-warning-primary border-border-warning' : 'bg-bg-secondary border-border-primary'}`}>
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-bg-v2-xs font-bg-v2-semibold font-bg-v2-sans ${isHuman ? 'bg-bg-warning-secondary text-text-warning' : 'bg-bg-brand-primary-alt text-text-brand-primary'}`}>
                            {idx + 1}
                          </span>
                          <div className="flex flex-col gap-bg-v2-xxs flex-1">
                            {isHuman && (
                              <Text variant="text-xs" weight="semibold" className="text-text-warning uppercase tracking-wide font-bg-v2-sans">
                                Human Required
                              </Text>
                            )}
                            <Text variant="text-sm" weight="regular" color="secondary" className="break-words">
                              {label}
                            </Text>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </>
            )}

          </div>
        )}
      </div>
    </div>

    {/* Delete confirmation */}
    <Modal
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      title="Delete Ticket"
      size="sm"
      onSubmit={() => { setShowDeleteConfirm(false); onDelete(ticket); }}
      submitLabel="Delete"
      submitVariant="outline"
      submitClassName="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        Are you sure you want to delete <strong>{ticket.id}</strong>? This action cannot be undone.
      </Text>
    </Modal>
    </>
  );
};

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-bg-v2-xs">
    <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
      {label}
    </Text>
    <div>{children}</div>
  </div>
);

// ─── Sidebar nav item — styled to match GroupItem ─────────────────────────────

const SidebarItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  count?: number;
  busy?: boolean;
  onClick: () => void;
}> = ({ icon, label, active, count, busy, onClick }) => (
  <div
    onClick={onClick}
    className={`flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm cursor-pointer transition-colors duration-200
      ${active ? 'bg-bg-brand-primary-alt' : 'hover:bg-bg-brand-primary-alt'}
    `}
  >
    <div className="flex-shrink-0 text-text-tertiary">{icon}</div>
    <div className="flex-1 min-w-0">
      <Text variant="text-sm" weight="semibold" color="secondary" className="truncate block">
        {label}
      </Text>
    </div>
    {busy ? (
      <span
        title="Organizing tickets into Epics…"
        className="w-3.5 h-3.5 flex-shrink-0 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin"
      />
    ) : count !== undefined && (
      <span className={`px-bg-v2-sm py-bg-v2-xxs rounded-full font-bg-v2-sans font-bg-v2-medium text-bg-v2-xs flex-shrink-0
        ${active ? 'bg-bg-brand-solid text-text-white' : 'bg-bg-secondary text-text-tertiary'}
      `}>
        {count}
      </span>
    )}
  </div>
);

// ─── Epics (Build Order) View ───────────────────────────────────────────────
// Ordered, collapsible list of epics. Epic name is the title; the wave number
// sits on the right; tickets are listed underneath when expanded.

// Compact searchable "move to epic" picker for a ticket row — lets a ticket be
// reassigned in one click without dragging across a long build order. Sourced
// from the already-loaded epic list (no extra fetch); null = Unassigned.
const TicketMovePicker: React.FC<{
  epics: Epic[];
  currentEpicId?: string;
  onPick: (epicId: string | null) => void;
}> = ({ epics, currentEpicId, onPick }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? epics.filter(e => e.name.toLowerCase().includes(q)) : epics;
  const pick = (id: string | null) => { setOpen(false); setQuery(''); onPick(id); };
  const toggle = () => {
    if (!open && btnRef.current) {
      // Open upward when there isn't room below (e.g. bottom rows / Unassigned).
      const rect = btnRef.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 340);
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref} className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        title="Move to epic"
        onClick={toggle}
        className="flex items-center justify-center text-text-tertiary hover:text-text-brand-primary transition-colors"
      >
        <Icon name={IconName.DotsHorizontal} size="sm" inheritColor />
      </button>
      {open && (
        <div className={`absolute right-0 z-30 w-[280px] rounded-bg-v2-md border border-border-primary bg-bg-primary shadow-lg overflow-hidden ${openUp ? 'bottom-full mb-bg-v2-xs' : 'top-full mt-bg-v2-xs'}`}>
          <div className="p-bg-v2-sm border-b border-border-secondary">
            <Input value={query} placeholder="Search epics…" autoFocus onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="max-h-[260px] overflow-y-auto py-bg-v2-xs modern-scrollbar">
            {filtered.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => pick(e.id)}
                disabled={e.id === currentEpicId}
                className={`w-full text-left px-bg-v2-md py-bg-v2-sm flex items-center gap-bg-v2-sm ${e.id === currentEpicId ? 'bg-bg-secondary opacity-60 cursor-default' : 'hover:bg-bg-secondary'}`}
              >
                <span className="text-text-tertiary text-bg-v2-xs w-[56px] flex-shrink-0 font-bg-v2-sans">Wave {e.order}</span>
                <span className="truncate text-bg-v2-sm text-text-primary font-bg-v2-sans">{e.name}</span>
              </button>
            ))}
            {!filtered.length && (
              <div className="px-bg-v2-md py-bg-v2-sm"><Text variant="text-sm" color="tertiary">No matching epics.</Text></div>
            )}
            {currentEpicId && (
              <button
                type="button"
                onClick={() => pick(null)}
                className="w-full text-left px-bg-v2-md py-bg-v2-sm hover:bg-bg-secondary border-t border-border-secondary"
              >
                <Text variant="text-sm" color="tertiary">Remove from epic (Unassigned)</Text>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EpicsView: React.FC<{
  projectId: string;
  canEdit: boolean;
  generating?: boolean;
  onRegenerate?: () => void;
  onSelectTicket: (t: Ticket) => void;
  onCountChange?: (count: number) => void;
  onTicketStatusChange?: (ticketId: string, status: TicketStatus) => void;
  reloadSignal?: number;
  focusEpicId?: string | null;
}> = ({ projectId, canEdit, generating, onRegenerate, onSelectTicket, onCountChange, onTicketStatusChange, reloadSignal, focusEpicId }) => {
  const [groups, setGroups] = useState<EpicGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reordering, setReordering] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);   // real-epic index being dragged
  const [dropIdx, setDropIdx] = useState<number | null>(null);   // insertion slot (0..n) where the gap opens
  const [dragTicketId, setDragTicketId] = useState<string | null>(null);  // ticket being dragged (within/between epics)
  const [ticketDropKey, setTicketDropKey] = useState<string | null>(null); // epic group highlighted as drop target
  const [ticketDropIdx, setTicketDropIdx] = useState<number | null>(null); // insertion slot within the target group (null = end)
  const [creatingEpic, setCreatingEpic] = useState(false);
  const [newEpicName, setNewEpicName] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Epic | null>(null);   // epic pending delete-confirm
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [editingEpicId, setEditingEpicId] = useState<string | null>(null); // epic being renamed inline
  const [editEpicName, setEditEpicName] = useState('');
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [savingStatusIds, setSavingStatusIds] = useState<Set<string>>(new Set()); // tickets with an in-flight status PATCH

  const keyOf = (g: EpicGroup) => g.epic?.id ?? 'unassigned';

  // Initial load: fetch grouped epics and expand the first epic by default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const g = await apiListEpicsGrouped(projectId);
        if (cancelled) return;
        setGroups(g);
        onCountChange?.(g.filter(x => x.epic).length);
        // Start fully collapsed; only a ?epic= deep-link expands one (see below).
        setExpanded(new Set());
      } catch (err) {
        console.error('Failed to load epics:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent refresh when a ticket is saved elsewhere (e.g. status changed in the
  // detail panel). Refetch grouped data but preserve the user's expanded state.
  useEffect(() => {
    if (!reloadSignal || !projectId) return;   // skip initial mount (reloadSignal starts at 0)
    let cancelled = false;
    (async () => {
      try {
        const g = await apiListEpicsGrouped(projectId);
        if (cancelled) return;
        setGroups(g);
        onCountChange?.(g.filter(x => x.epic).length);
      } catch (err) {
        console.error('Failed to refresh epics:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from a Build-mode epic chip (?epic=<id>): once groups are loaded,
  // expand that epic and scroll it into view. Guarded so a later reloadSignal
  // refetch (or the user collapsing it) doesn't re-force it open.
  const focusedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusEpicId || !groups.length || focusedRef.current === focusEpicId) return;
    if (!groups.some(g => g.epic?.id === focusEpicId)) return;
    focusedRef.current = focusEpicId;
    setExpanded(new Set([focusEpicId]));
    // Effects run post-paint, so the group element is already in the DOM.
    document.getElementById(`epic-group-${focusEpicId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusEpicId, groups]);

  const toggle = (k: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const expandAll = () => setExpanded(new Set(groups.map(keyOf)));
  const collapseAll = () => setExpanded(new Set());

  // Create a new (empty) epic as the last wave, then expand it so tickets can be
  // dragged in. It persists immediately (createEpic appends at the end).
  const addEpic = async () => {
    const name = newEpicName.trim();
    if (!name || creatingBusy) return;
    setCreatingBusy(true);
    try {
      const epic = await apiCreateEpic(projectId, name);
      setGroups(prev => {
        const real = prev.filter(g => g.epic);
        const unassigned = prev.filter(g => !g.epic);
        return [...real, { epic, done: 0, tickets: [] }, ...unassigned];
      });
      onCountChange?.(groups.filter(g => g.epic).length + 1);
      setExpanded(prev => new Set(prev).add(epic.id));
      setCreatingEpic(false);
      setNewEpicName('');
    } catch (err) {
      console.error('Failed to create epic:', err);
    } finally {
      setCreatingBusy(false);
    }
  };

  // Move an epic earlier/later in the build order (changes its wave). Optimistic
  // local reorder + renumber, then persist; refetch to recover on failure.
  const moveEpic = async (fromIdx: number, toIdx: number) => {
    const real = groups.filter(g => g.epic);
    if (reordering || fromIdx === toIdx || toIdx < 0 || toIdx >= real.length) return;
    const reordered = [...real];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const renumbered = reordered.map((g, i) => ({ ...g, epic: g.epic ? { ...g.epic, order: i + 1 } : g.epic }));
    setGroups([...renumbered, ...groups.filter(g => !g.epic)]);
    setReordering(true);
    try {
      await apiReorderEpics(projectId, reordered.map(g => g.epic!.id));
    } catch (err) {
      console.error('Failed to reorder epics:', err);
      try { setGroups(await apiListEpicsGrouped(projectId)); } catch { /* ignore */ }
    } finally {
      setReordering(false);
    }
  };

  // Delete an epic — its tickets fall back to Unassigned. Optimistic: move the
  // tickets into the Unassigned group, drop the epic, renumber the waves, then
  // persist; refetch to recover on failure.
  const removeEpic = async (epic: Epic) => {
    if (deletingBusy) return;
    setDeletingBusy(true);
    const prev = groups;
    const target = groups.find(g => g.epic?.id === epic.id);
    const orphaned = (target?.tickets ?? []).map(t => ({ ...t, epicId: undefined, epic: undefined, buildWave: undefined }));
    const remainingReal = groups
      .filter(g => g.epic && g.epic.id !== epic.id)
      .map((g, i) => ({ ...g, epic: { ...g.epic!, order: i + 1 } }));
    const existing = groups.find(g => !g.epic);
    const mergedTickets = [...(existing?.tickets ?? []), ...orphaned];
    const unassigned: EpicGroup = {
      epic: null,
      tickets: mergedTickets,
      done: mergedTickets.filter(t => t.status === 'Closed').length,
    };
    setGroups(remainingReal.length || mergedTickets.length ? [...remainingReal, unassigned] : []);
    onCountChange?.(remainingReal.length);
    setDeleteTarget(null);
    try {
      await apiDeleteEpic(projectId, epic.id);
    } catch (err) {
      console.error('Failed to delete epic:', err);
      setGroups(prev);
      onCountChange?.(prev.filter(g => g.epic).length);
    } finally {
      setDeletingBusy(false);
    }
  };

  const startRename = (epic: Epic) => { setEditingEpicId(epic.id); setEditEpicName(epic.name); };
  const cancelRename = () => { setEditingEpicId(null); setEditEpicName(''); };

  // Rename an epic. Optimistic local update, then persist; refetch on failure.
  const commitRename = async (epic: Epic) => {
    const name = editEpicName.trim();
    if (!name || name === epic.name) { cancelRename(); return; }
    const prev = groups;
    setGroups(gs => gs.map(g => (g.epic?.id === epic.id ? { ...g, epic: { ...g.epic!, name } } : g)));
    cancelRename();
    try {
      await apiRenameEpic(projectId, epic.id, name);
    } catch (err) {
      console.error('Failed to rename epic:', err);
      setGroups(prev);
    }
  };

  // Show the spinner while the initial fetch runs, or while generation is
  // running and there are no epics to display yet.
  if (loading || (generating && !groups.length)) {
    return (
      <div className="flex flex-1 items-center justify-center flex-col gap-bg-v2-md">
        <div className="w-6 h-6 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
        <Text variant="text-sm" weight="regular" color="tertiary">Organizing tickets into Epics…</Text>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="flex flex-1 items-center justify-center px-bg-v2-xl">
        <Text variant="text-sm" weight="regular" color="tertiary">
          No epics yet — they’re generated automatically once tickets are created.
        </Text>
      </div>
    );
  }

  const realEpics = groups.filter(g => g.epic);
  const unassignedGroups = groups.filter(g => !g.epic);
  const epicCount = realEpics.length;

  const resetDrag = () => { setDragIdx(null); setDropIdx(null); };
  // While dragging, work out which slot the cursor is over (top half → before
  // this row, bottom half → after it) so the gap opens at the right place.
  const onRowDragOver = (e: React.DragEvent, realIdx: number) => {
    if (dragIdx === null) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const after = (e.clientY - rect.top) > rect.height / 2;
    const idx = after ? realIdx + 1 : realIdx;
    if (idx !== dropIdx) setDropIdx(idx);
  };
  const commitDrop = () => {
    if (dragIdx !== null && dropIdx !== null) {
      const to = dragIdx < dropIdx ? dropIdx - 1 : dropIdx;   // account for removal shift
      moveEpic(dragIdx, to);
    }
    resetDrag();
  };
  // Dropping right where it came from is a no-op — don't open a gap for it.
  const dropIsNoop = dragIdx !== null && dropIdx !== null && (dropIdx === dragIdx || dropIdx === dragIdx + 1);

  const Gap = () => (
    <div className="mx-bg-v2-lg my-bg-v2-sm h-[46px] rounded-bg-v2-md border-2 border-dashed border-border-brand bg-bg-brand-primary-alt" />
  );

  // Insertion indicator between ticket rows: a 2px brand line drawn at a row's
  // top or bottom edge. It's absolutely positioned (zero flow height) so it
  // NEVER reflows the list — a reflow under the cursor is what caused the drag
  // to flicker (gap appears → pushes the row → cursor lands off it → gap
  // vanishes → repeat). A stable layout keeps the computed drop slot steady.
  const TicketDropLine = ({ edge }: { edge: 'top' | 'bottom' }) => (
    <div className={`pointer-events-none absolute left-[40px] right-[16px] z-10 border-t-2 border-border-brand ${edge === 'top' ? 'top-0' : 'bottom-0'}`} />
  );

  // While dragging a ticket over a row, open the insertion slot at the top or
  // bottom half of that row (0..n over the group's current list). stopPropagation
  // keeps the parent group's "drop at end" handler from also firing.
  const onTicketRowDragOver = (e: React.DragEvent, groupKey: string, rowIdx: number) => {
    if (!dragTicketId) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = (e.clientY - rect.top) > rect.height / 2 ? rowIdx + 1 : rowIdx;
    if (ticketDropKey !== groupKey) setTicketDropKey(groupKey);
    if (ticketDropIdx !== idx) setTicketDropIdx(idx);
  };

  // Move a ticket into another epic (or to Unassigned). Optimistic move between
  // groups + done-count recompute, then persist; refetch to recover on failure.
  const reassignTicket = async (ticketId: string, target: EpicGroup) => {
    const targetEpicId = target.epic?.id ?? null;
    let moved: Ticket | undefined;
    const stripped = groups.map(grp => {
      const found = grp.tickets.find(t => t.id === ticketId);
      if (found) { moved = found; return { ...grp, tickets: grp.tickets.filter(t => t.id !== ticketId) }; }
      return grp;
    });
    if (!moved || (moved.epicId ?? null) === targetEpicId) return;   // not found / same epic
    const updated: Ticket = { ...moved, epicId: targetEpicId ?? undefined, epic: target.epic?.name, buildWave: target.epic?.order };
    const placed = stripped.map(grp =>
      keyOf(grp) === keyOf(target) ? { ...grp, tickets: [...grp.tickets, updated] } : grp
    );
    setGroups(placed.map(grp => ({ ...grp, done: grp.tickets.filter(t => t.status === 'Closed').length })));
    try {
      await apiUpdateTicket(projectId, ticketId, { epic_id: targetEpicId });
    } catch (err) {
      console.error('Failed to reassign ticket epic:', err);
      try { setGroups(await apiListEpicsGrouped(projectId)); } catch { /* ignore */ }
    }
  };

  // Reassign via the row picker — resolve the chosen epic id to its group and
  // reuse the same optimistic move path as drag-and-drop. null = Unassigned.
  const moveTicketTo = (ticketId: string, epicId: string | null) => {
    const target = epicId ? groups.find(g => g.epic?.id === epicId) : groups.find(g => !g.epic);
    if (target) reassignTicket(ticketId, target);
  };

  // Unified drop handler for the ticket drag-and-drop. `slot` is the insertion
  // index within the target group's current list (null = drop at the end / on
  // the header). Same-epic → reorder; different real epic → move AND position
  // (one reorder call stamps epic_id + epic_order); Unassigned target → just
  // unassign (intra-epic order isn't persisted there). Optimistic, reconciled
  // from the server's refreshed grouped view; refetch on failure.
  const dropTicket = async (target: EpicGroup, slot: number | null) => {
    const ticketId = dragTicketId;
    setDragTicketId(null); setTicketDropKey(null); setTicketDropIdx(null);
    if (!ticketId) return;

    const source = groups.find(grp => grp.tickets.some(t => t.id === ticketId));
    const moved = source?.tickets.find(t => t.id === ticketId);
    if (!moved || !source) return;

    // Dropping into Unassigned has no per-epic order to persist — only unassign
    // if the ticket is actually leaving an epic (dragging within Unassigned is a no-op).
    if (!target.epic) {
      if (source.epic) reassignTicket(ticketId, target);
      return;
    }

    const targetKey = keyOf(target);
    const sameEpic = keyOf(source) === targetKey;

    // Build the target epic's new ordered ticket-id list with `moved` placed at `slot`.
    let orderedIds: string[];
    if (sameEpic) {
      const arr = [...target.tickets];
      const fromIdx = arr.findIndex(t => t.id === ticketId);
      const dropSlot = slot == null ? arr.length : slot;
      if (fromIdx === dropSlot || fromIdx + 1 === dropSlot) return;   // dropped where it already is
      const [m] = arr.splice(fromIdx, 1);
      arr.splice(fromIdx < dropSlot ? dropSlot - 1 : dropSlot, 0, m); // account for removal shift
      orderedIds = arr.map(t => t.id);
    } else {
      const arr = target.tickets.filter(t => t.id !== ticketId);
      arr.splice(slot == null ? arr.length : Math.min(slot, arr.length), 0, moved);
      orderedIds = arr.map(t => t.id);
    }

    // Optimistic: reorder the target group (and remove from the source on a
    // cross-epic move), keeping done-counts and the moved ticket's epic in sync.
    const prev = groups;
    const updatedMoved: Ticket = { ...moved, epicId: target.epic.id, epic: target.epic.name, buildWave: target.epic.order };
    const next = groups.map(grp => {
      if (keyOf(grp) === targetKey) {
        const byId = new Map(grp.tickets.map(t => [t.id, t] as const));
        byId.set(updatedMoved.id, updatedMoved);
        const tickets = orderedIds.map(id => byId.get(id)).filter((t): t is Ticket => !!t);
        return { ...grp, tickets, done: tickets.filter(t => t.status === 'Closed').length };
      }
      if (!sameEpic && keyOf(grp) === keyOf(source)) {
        const tickets = grp.tickets.filter(t => t.id !== ticketId);
        return { ...grp, tickets, done: tickets.filter(t => t.status === 'Closed').length };
      }
      return grp;
    });
    setGroups(next);
    try {
      setGroups(await apiReorderEpicTickets(projectId, target.epic.id, orderedIds));
    } catch (err) {
      console.error('Failed to reorder epic tickets:', err);
      setGroups(prev);
    }
  };

  // Inline status change from an epic's ticket row — optimistic with revert,
  // keeping each group's done-count in sync. Mirrors the list-view handler.
  const changeTicketStatus = async (ticket: Ticket, newStatus: TicketStatus) => {
    if (newStatus === ticket.status || savingStatusIds.has(ticket.id)) return;
    const applyStatus = (status: TicketStatus) => (gs: EpicGroup[]) => gs.map(grp => {
      if (!grp.tickets.some(t => t.id === ticket.id)) return grp;
      const tickets = grp.tickets.map(t => t.id === ticket.id ? { ...t, status } : t);
      return { ...grp, tickets, done: tickets.filter(t => t.status === 'Closed').length };
    });
    setGroups(applyStatus(newStatus));
    onTicketStatusChange?.(ticket.id, newStatus);
    setSavingStatusIds(prev => new Set(prev).add(ticket.id));
    try {
      await apiUpdateTicket(projectId, ticket.id, { status: newStatus });
    } catch (err) {
      console.error('Failed to update ticket status inline:', err);
      setGroups(applyStatus(ticket.status));
      onTicketStatusChange?.(ticket.id, ticket.status);
    } finally {
      setSavingStatusIds(prev => { const next = new Set(prev); next.delete(ticket.id); return next; });
    }
  };

  const renderGroup = (g: EpicGroup, realIdx: number) => {
    const k = keyOf(g);
    const isOpen = expanded.has(k);
    const isUnassigned = !g.epic;
    const isEditing = !!g.epic && editingEpicId === g.epic.id;
    const isDraggable = canEdit && !!g.epic && !isEditing;
    const isDragging = dragIdx === realIdx && realIdx !== -1;
    const isTicketDropTarget = !!dragTicketId && ticketDropKey === k;
    // Where (if anywhere) to draw the ticket insertion placeholder in this group.
    // dragFromIdx is the dragged ticket's index if it lives in THIS group (-1 when
    // it's being dragged in from elsewhere); a same-group drop next to its origin
    // is a no-op, so no gap is drawn there.
    const dragFromIdx = dragTicketId ? g.tickets.findIndex(t => t.id === dragTicketId) : -1;
    const ticketDropNoop = dragFromIdx !== -1 && ticketDropIdx !== null && (ticketDropIdx === dragFromIdx || ticketDropIdx === dragFromIdx + 1);
    const showTicketGapAt = (idx: number) => !!dragTicketId && ticketDropKey === k && ticketDropIdx === idx && !ticketDropNoop;
    return (
      <div
        key={k}
        id={g.epic ? `epic-group-${g.epic.id}` : undefined}
        onDragOver={dragTicketId ? (e) => { e.preventDefault(); if (ticketDropKey !== k) setTicketDropKey(k); if (ticketDropIdx !== null) setTicketDropIdx(null); } : undefined}
        onDrop={dragTicketId ? (e) => { e.preventDefault(); dropTicket(g, ticketDropIdx); } : undefined}
        className={isTicketDropTarget ? 'ring-2 ring-inset ring-border-brand rounded-bg-v2-md' : ''}
      >
        {/* Epic header row — drag the grip to change its wave; name left, wave right */}
        <div
          draggable={isDraggable}
          onClick={() => toggle(k)}
          onDragStart={isDraggable ? () => { setDragIdx(realIdx); setDropIdx(realIdx); } : undefined}
          onDragOver={isDraggable ? (e) => onRowDragOver(e, realIdx) : undefined}
          onDrop={isDraggable ? (e) => { e.preventDefault(); commitDrop(); } : undefined}
          onDragEnd={resetDrag}
          className={`flex items-center gap-bg-v2-md px-bg-v2-lg py-bg-v2-md cursor-pointer border-b transition-all duration-150 ${isDragging ? 'opacity-30' : ''} ${isTicketDropTarget ? 'bg-bg-brand-primary-alt border-border-brand' : 'bg-bg-secondary hover:bg-bg-tertiary border-border-primary'}`}
        >
          {isDraggable && (
            <span
              title="Drag to reorder waves"
              className="grid grid-cols-2 gap-[2px] flex-shrink-0 cursor-grab active:cursor-grabbing text-text-tertiary"
              onClick={(e) => e.stopPropagation()}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="w-[3px] h-[3px] rounded-full bg-current" />
              ))}
            </span>
          )}
          <span className={`flex items-center justify-center flex-shrink-0 text-text-tertiary transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}>
            <Icon name={IconName.ChevronRight} size="sm" inheritColor />
          </span>
          {isEditing ? (
            <div className="flex-1 min-w-0 flex items-center gap-bg-v2-sm" onClick={(e) => e.stopPropagation()}>
              <div className="flex-1 min-w-0">
                <Input
                  value={editEpicName}
                  autoFocus
                  onChange={(e) => setEditEpicName(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') commitRename(g.epic!);
                    if (e.key === 'Escape') cancelRename();
                  }}
                />
              </div>
              <Button label="Save" variant="primary" size="small" onClick={() => commitRename(g.epic!)} disabled={!editEpicName.trim()} />
              <Button label="Cancel" variant="outline" size="small" onClick={cancelRename} />
            </div>
          ) : (
            <div className="flex items-center gap-bg-v2-sm min-w-0">
              {g.epic && (
                <span className="flex-shrink-0 font-bg-v2-sans font-bg-v2-semibold text-bg-v2-xs text-text-tertiary tabular-nums">
                  {epicCode(g.epic.seq)}
                </span>
              )}
              <Text variant="text-sm" weight="semibold" color={isUnassigned ? 'tertiary' : 'primary'} className="truncate">
                {g.epic ? g.epic.name : 'Unassigned'}
              </Text>
            </div>
          )}
          <div className="ml-auto flex items-center gap-bg-v2-lg flex-shrink-0">
            <Text variant="text-sm" weight="regular" color="tertiary">{g.done}/{g.tickets.length} done</Text>
            {g.epic && (
              <span className="px-bg-v2-md py-bg-v2-xxs rounded-full bg-bg-brand-primary-alt text-text-brand-primary text-bg-v2-xs font-bg-v2-sans font-bg-v2-semibold whitespace-nowrap">
                Wave {g.epic.order}
              </span>
            )}
            {canEdit && g.epic && !isEditing && (
              <button
                type="button"
                title="Rename epic"
                onClick={(e) => { e.stopPropagation(); startRename(g.epic!); }}
                className="flex items-center justify-center flex-shrink-0 text-text-tertiary hover:text-text-brand-primary transition-colors"
              >
                <Icon name={IconName.Edit} size="sm" inheritColor />
              </button>
            )}
            {canEdit && g.epic && !isEditing && (
              <button
                type="button"
                title="Delete epic"
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(g.epic!); }}
                className="flex items-center justify-center flex-shrink-0 text-text-tertiary hover:text-text-error-primary transition-colors"
              >
                <Icon name={IconName.Delete} size="sm" inheritColor />
              </button>
            )}
          </div>
        </div>

        {/* Tickets — indented under the epic (hidden while this epic is dragging) */}
        {isOpen && !isDragging && g.tickets.length === 0 && (
          <div className="pl-[52px] pr-bg-v2-lg py-bg-v2-md border-b border-border-secondary">
            <Text variant="text-sm" weight="regular" color="tertiary">No tickets.</Text>
          </div>
        )}
        {isOpen && !isDragging && g.tickets.map((t, ti) => (
          <div key={t.id} className="relative">
          {showTicketGapAt(ti) && <TicketDropLine edge="top" />}
          {ti === g.tickets.length - 1 && showTicketGapAt(g.tickets.length) && <TicketDropLine edge="bottom" />}
          <div
            draggable={canEdit}
            onClick={() => onSelectTicket(t)}
            onDragStart={canEdit ? (e) => { e.stopPropagation(); setDragTicketId(t.id); } : undefined}
            onDragOver={canEdit && dragTicketId ? (e) => onTicketRowDragOver(e, k, ti) : undefined}
            onDrop={canEdit && dragTicketId ? (e) => { e.preventDefault(); e.stopPropagation(); dropTicket(g, ticketDropIdx); } : undefined}
            onDragEnd={() => { setDragTicketId(null); setTicketDropKey(null); setTicketDropIdx(null); }}
            className={`flex items-center gap-bg-v2-md pl-[40px] pr-bg-v2-lg py-bg-v2-md cursor-pointer border-b border-border-secondary hover:bg-bg-secondary ${dragTicketId === t.id ? 'opacity-40' : ''}`}
          >
            {canEdit && (
              <span
                title="Drag to reorder within this epic, or move to another epic"
                className="grid grid-cols-2 gap-[2px] flex-shrink-0 cursor-grab active:cursor-grabbing text-text-tertiary"
                onClick={(e) => e.stopPropagation()}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className="w-[3px] h-[3px] rounded-full bg-current" />
                ))}
              </span>
            )}
            <Text variant="text-sm" weight="regular" color="secondary" className="flex-shrink-0 w-[72px]">{t.id}</Text>
            <Text variant="text-sm" weight="regular" color="primary" className="flex-1 truncate">{t.title}</Text>
            {canEdit ? (
              <span
                className="flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
                onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <InlineStatusSelect
                  status={t.status}
                  saving={savingStatusIds.has(t.id)}
                  onChange={(s) => changeTicketStatus(t, s)}
                />
              </span>
            ) : (
              <Text variant="text-sm" weight="regular" color="secondary" className="flex-shrink-0 whitespace-nowrap">{t.status}</Text>
            )}
            {t.assignTo
              ? <Text variant="text-sm" weight="regular" color="secondary" className="flex-shrink-0 w-[120px] truncate text-right">{t.assignTo.name}</Text>
              : <Text variant="text-sm" weight="regular" color="tertiary" className="flex-shrink-0 w-[120px] text-right">—</Text>}
            {canEdit && realEpics.length > 0 && (
              <TicketMovePicker
                epics={realEpics.map(grp => grp.epic!)}
                currentEpicId={t.epicId}
                onPick={(id) => moveTicketTo(t.id, id)}
              />
            )}
          </div>
          </div>
        ))}
      </div>
    );
  };

  const dragging = dragIdx !== null;

  return (
    <>
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Header — matches the All Tickets toolbar height */}
      <div className="flex items-center h-[60px] px-bg-v2-xl border-b border-border-secondary flex-shrink-0 gap-bg-v2-md">
        <Text variant="text-sm" weight="semibold" color="primary">All Epics ({epicCount})</Text>
        <div className="ml-auto flex items-center gap-bg-v2-md">
          {creatingEpic ? (
            <>
              <Input
                value={newEpicName}
                placeholder="New epic name"
                autoFocus
                onChange={(e) => setNewEpicName(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') addEpic();
                  if (e.key === 'Escape') { setCreatingEpic(false); setNewEpicName(''); }
                }}
              />
              <Button label="Add" variant="primary" size="small" onClick={addEpic} disabled={creatingBusy || !newEpicName.trim()} />
              <Button label="Cancel" variant="outline" size="small" onClick={() => { setCreatingEpic(false); setNewEpicName(''); }} />
            </>
          ) : (
            <>
              <button type="button" onClick={expandAll} className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-brand-primary hover:underline">Expand all</button>
              <span className="text-text-tertiary select-none">|</span>
              <button type="button" onClick={collapseAll} className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-brand-primary hover:underline">Collapse all</button>
              {canEdit && (
                <Button
                  label={<span className="flex items-center gap-bg-v2-xs whitespace-nowrap"><Icon name={IconName.Reload} size="sm" inheritColor />{generating ? 'Re-evaluating…' : 'Re-evaluate'}</span>}
                  variant="outline"
                  size="small"
                  onClick={() => setShowRegenConfirm(true)}
                  disabled={generating}
                />
              )}
              {canEdit && (
                <Button
                  label={<span className="flex items-center gap-bg-v2-xs whitespace-nowrap"><Icon name={IconName.Plus} size="sm" inheritColor />New Epic</span>}
                  variant="primary"
                  size="small"
                  onClick={() => setCreatingEpic(true)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Re-evaluation in progress while epics are already shown — the list
          refreshes automatically when it finishes. */}
      {generating && (
        <div className="flex items-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-sm border-b border-border-primary bg-bg-brand-primary-alt flex-shrink-0">
          <div className="w-3.5 h-3.5 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin flex-shrink-0" />
          <Text variant="text-sm" weight="regular" color="secondary">
            Organizing tickets into Epics… This list will refresh when it's done.
          </Text>
        </div>
      )}

      {/* Accordion — full-width rows in the same scale as the tickets table.
          While dragging, a gap opens at the cursor's slot; drop to place there. */}
      <div
        className="flex-1 overflow-y-auto modern-scrollbar-spaced"
        onDragOver={dragging ? (e) => e.preventDefault() : undefined}
        onDrop={dragging ? (e) => { e.preventDefault(); commitDrop(); } : undefined}
      >
        {realEpics.map((g, i) => (
          <React.Fragment key={g.epic!.id}>
            {dragging && dropIdx === i && !dropIsNoop && <Gap />}
            {renderGroup(g, i)}
          </React.Fragment>
        ))}
        {dragging && dropIdx === realEpics.length && !dropIsNoop && <Gap />}
        {unassignedGroups.map((g) => renderGroup(g, -1))}
      </div>
    </div>

    {/* Delete-epic confirmation — its tickets move to Unassigned */}
    <Modal
      isOpen={!!deleteTarget}
      onClose={() => setDeleteTarget(null)}
      title="Delete Epic"
      size="sm"
      onSubmit={() => { if (deleteTarget) removeEpic(deleteTarget); }}
      onCancel={() => setDeleteTarget(null)}
      submitLabel="Delete"
      submitVariant="outline"
      submitClassName="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        Delete <strong>{deleteTarget?.name}</strong>? Its {groups.find(g => g.epic?.id === deleteTarget?.id)?.tickets.length ?? 0} ticket(s) will move to
        {' '}<strong>Unassigned</strong> and the remaining waves will be renumbered. This action cannot be undone.
      </Text>
    </Modal>

    {/* Re-evaluate build order confirmation — wipes and rebuilds all epics */}
    <Modal
      isOpen={showRegenConfirm}
      onClose={() => setShowRegenConfirm(false)}
      title="Re-evaluate Build Order"
      size="sm"
      onSubmit={() => { onRegenerate?.(); setShowRegenConfirm(false); }}
      onCancel={() => setShowRegenConfirm(false)}
      submitLabel="Re-evaluate"
      submitVariant="primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        This will <strong>delete all existing epics and re-create them from scratch</strong>, re-organizing every ticket into
        fresh epics using AI. Any manual changes — renames, custom epics, and ticket assignments —
        {' '}<strong>will be lost</strong>. This action cannot be undone.
      </Text>
    </Modal>
    </>
  );
};

// ─── Sortable column header ───────────────────────────────────────────────────

const ThSortable: React.FC<{
  field: SortField;
  active: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
  children: React.ReactNode;
}> = ({ field, active, dir, onSort, children }) => (
  <th
    onClick={() => onSort(field)}
    className="px-bg-v2-lg py-bg-v2-md text-left cursor-pointer select-none group whitespace-nowrap"
  >
    <div className="flex items-center gap-bg-v2-xs">
      <span className="font-bg-v2-sans font-bg-v2-semibold text-bg-v2-sm uppercase tracking-wide text-text-tertiary group-hover:text-text-secondary transition-colors">
        {children}
      </span>
      <SortIcon field={field} active={active} dir={dir} />
    </div>
  </th>
);

// ─── Assignee autocomplete ────────────────────────────────────────────────────

export interface AssigneeOption {
  value: string;
  label: string;
  email: string;
  role: string;
  pending?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  owner: 'text-text-brand-primary',
  project_collaborator: 'text-text-secondary',
  project_viewer: 'text-text-tertiary',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  project_collaborator: 'Collaborator',
  project_viewer: 'Viewer',
};

const AssigneeCombobox: React.FC<{
  value: ApiAssignTo | null | undefined;
  options: AssigneeOption[];
  onChange: (val: ApiAssignTo | null) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Match selected option by email (canonical key stored in DB)
  const selected = value?.email ? options.find(o => o.email === value.email) ?? null : null;

  const handleBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setOpen(false);
  };

  const select = (opt: AssigneeOption) => {
    onChange({ name: opt.label, email: opt.email });
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const displayText = value
    ? value.email
      ? `${value.name} (${value.email})`
      : value.name
    : null;

  return (
    <div ref={containerRef} className="relative flex flex-col gap-bg-v2-xs" onBlur={handleBlur}>
      <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">
        Assign To
      </label>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md bg-bg-primary focus:outline-none focus:border-border-brand transition-colors text-left"
      >
        <span className={`text-bg-v2-sm font-bg-v2-sans font-bg-v2-regular truncate ${displayText ? 'text-text-primary' : 'text-text-placeholder'}`}>
          {displayText ?? 'Select assignee…'}
        </span>
        <span className="flex items-center gap-bg-v2-xs shrink-0 ml-bg-v2-sm">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={e => e.key === 'Enter' && clear(e as unknown as React.MouseEvent)}
              className="text-text-tertiary hover:text-text-secondary"
            >
              <Icon name={IconName.Cross} size="sm" inheritColor />
            </span>
          )}
          <span className={`transition-transform duration-150 text-text-tertiary ${open ? 'rotate-180' : ''}`}>
            <Icon name={IconName.ChevronDown} size="sm" inheritColor />
          </span>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-[2px] bg-bg-primary border border-border-primary rounded-bg-v2-md container-shadow-sm overflow-hidden max-h-[240px] overflow-y-auto modern-scrollbar">
          {options.length === 0 ? (
            <div className="px-bg-v2-lg py-bg-v2-sm">
              <Text variant="text-sm" color="tertiary">No members found</Text>
            </div>
          ) : (
            options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={e => { e.preventDefault(); select(opt); }}
                className={`w-full flex items-center justify-between px-bg-v2-lg py-bg-v2-sm hover:bg-bg-secondary transition-colors text-left ${opt.email === value?.email ? 'bg-bg-secondary' : ''}`}
              >
                <div className="flex items-center gap-bg-v2-sm min-w-0 truncate">
                  <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-primary truncate">
                    {opt.label}{opt.email ? ` (${opt.email})` : ''}
                  </span>
                  {opt.pending && (
                    <span className="shrink-0 text-bg-v2-xs font-bg-v2-sans font-bg-v2-medium text-text-warning bg-bg-warning-subtle px-bg-v2-sm py-[1px] rounded-full">
                      Pending
                    </span>
                  )}
                </div>
                <span className={`shrink-0 ml-bg-v2-sm text-bg-v2-xs font-bg-v2-sans font-bg-v2-medium ${ROLE_COLORS[opt.role] || 'text-text-tertiary'}`}>
                  {ROLE_LABELS[opt.role] || opt.role}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Ticket Form (shared for create & edit) ───────────────────────────────────

const TicketForm: React.FC<{
  form: Partial<Ticket>;
  formError: string;
  onChange: (patch: Partial<Ticket>) => void;
  onClearError: () => void;
  assigneeOptions?: AssigneeOption[];
  projectId?: string;   // when set, shows the Epic selector (create + edit)
}> = ({ form, formError, onChange, onClearError, assigneeOptions = [], projectId }) => (
  <div className="flex flex-col gap-bg-v2-lg">
    <Input
      label="Title"
      placeholder="Enter ticket title"
      value={form.title || ''}
      onChange={(e) => { onChange({ title: e.target.value }); onClearError(); }}
      errorText={formError}
      required
    />

    {projectId && (
      <EpicSelector
        label="Epic"
        projectId={projectId}
        epicId={form.epicId}
        onChange={(id) => onChange({ epicId: id ?? undefined })}
      />
    )}

    <div className="grid grid-cols-3 gap-bg-v2-lg">
      <Select
        label="Status"
        options={[
          { value: 'Open', label: 'Open' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Closed', label: 'Closed' },
        ]}
        value={form.status || 'Open'}
        onChange={(v) => onChange({ status: v as TicketStatus })}
      />
      <Select
        label="Priority"
        options={[
          { value: 'High', label: 'High' },
          { value: 'Medium', label: 'Medium' },
          { value: 'Low', label: 'Low' },
        ]}
        value={form.priority || 'Medium'}
        onChange={(v) => onChange({ priority: v as TicketPriority })}
      />
      <Select
        label="Work Type"
        placeholder="Select work type"
        options={[
          { value: 'User Story', label: 'User Story' },
          { value: 'Data Model - Reference', label: 'Data Model - Reference' },
          { value: 'Data Model - Tracked', label: 'Data Model - Tracked' },
          { value: 'User Interface', label: 'User Interface' },
          { value: 'Rule', label: 'Rule' },
          { value: 'Integration', label: 'Integration' },
          { value: 'Workflow', label: 'Workflow' },
          { value: 'Report', label: 'Report' },
          { value: 'Role', label: 'Role' },
          { value: 'UI Policy', label: 'UI Policy' },
          { value: 'Other', label: 'Other' },
        ]}
        value={form.workType || ''}
        onChange={(v) => onChange({ workType: v as TicketWorkType })}
      />
    </div>

    <div className="grid grid-cols-2 gap-bg-v2-lg">
      <Select
        label="Expertise"
        placeholder="Select expertise"
        options={[
          { value: 'Agent', label: 'Agent' },
          { value: 'Human', label: 'Human' },
          { value: 'Agent + Human', label: 'Agent + Human' },
        ]}
        value={form.expertise || ''}
        onChange={(v) => onChange({ expertise: v as TicketExpertise })}
      />
    </div>

    <div className="grid grid-cols-2 gap-bg-v2-lg">
      <AssigneeCombobox
        value={form.assignTo}
        options={assigneeOptions}
        onChange={(val) => onChange({ assignTo: val })}
      />
      <Input
        label="Due Date"
        type="date"
        value={form.dueDate || ''}
        onChange={(e) => onChange({ dueDate: e.target.value })}
        onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()}
      />
    </div>

    <Input
      label="CAB Reference"
      placeholder="e.g. CAB-2024-001 (optional)"
      value={form.cabReference || ''}
      onChange={(e) => onChange({ cabReference: e.target.value })}
    />

    <div className="flex flex-col gap-bg-v2-sm">
      <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">Description</label>
      <textarea
        placeholder="Enter ticket description"
        value={form.description || ''}
        onChange={(e) => onChange({ description: e.target.value })}
        rows={5}
        className="w-full px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md bg-bg-primary text-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-border-brand resize-y min-h-[100px]"
      />
    </div>

    <div className="flex flex-col gap-bg-v2-sm">
      <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">Note</label>
      <textarea
        placeholder="Reviewer note (optional)"
        value={form.note || ''}
        onChange={(e) => onChange({ note: e.target.value })}
        rows={3}
        className="w-full px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md bg-bg-primary text-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-border-brand resize-y min-h-[70px]"
      />
    </div>
  </div>
);

// ─── Compact modal footer ─────────────────────────────────────────────────────

const ModalFooter: React.FC<{
  submitLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ submitLabel, onCancel, onSubmit }) => (
  <div className="flex items-center justify-end gap-bg-v2-md pt-bg-v2-lg border-t border-border-secondary mt-bg-v2-lg">
    <Button label="Cancel" variant="outline" size="small" onClick={onCancel} />
    <Button label={submitLabel} variant="primary" size="small" onClick={onSubmit} />
  </div>
);

// ─── Main TicketsPage ─────────────────────────────────────────────────────────

type TicketView = 'all' | 'my' | 'epics';

interface TicketsPageProps {
  permissions?: string[];
  currentUserId?: string;
  projectPlatformType?: string;
  projectOwnerId?: string;
  projectOwnerName?: string;
  projectOwnerEmail?: string;
  onEvaluationComplete?: () => void;
}

const PLATFORM_TYPE_MAP: Record<string, TicketPlatformType> = {
  TylerAppPlatform: 'TAP',
  ServiceNow: 'SN',
  PowerApps: 'PA',
  Salesforce: 'SF',
  Web: 'Web',
};

const EMPTY_FORM: Partial<Ticket> = {
  status: 'Open',
  priority: 'Medium',
};

const TicketsPage: React.FC<TicketsPageProps> = ({ permissions = [], currentUserId, projectPlatformType, projectOwnerId, projectOwnerName, projectOwnerEmail, onEvaluationComplete }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Read initial state from URL ──────────────────────────────────────────
  const initPage      = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const initPageSize  = (PAGE_SIZE_OPTIONS as readonly number[]).includes(parseInt(searchParams.get('pageSize') || '25', 10))
    ? (parseInt(searchParams.get('pageSize') || '25', 10) as PageSize)
    : 25;
  const initSortField = (searchParams.get('sortField') || 'id') as SortField;
  const initSortDir   = (searchParams.get('sortDir') || 'asc') as SortDir;
  const initView      = (searchParams.get('view') || 'all') as TicketView;
  const initSearch    = searchParams.get('search') || '';
  const initStatus    = searchParams.getAll('status');
  const initPriority  = searchParams.getAll('priority');
  const initSource    = searchParams.getAll('source');
  const initWorkType  = searchParams.getAll('workType');
  const initExpertise = searchParams.getAll('expertise');

  const [view, setView]                   = useState<TicketView>(initView);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchInput, setSearchInput]     = useState(initSearch);
  const [searchQuery, setSearchQuery]     = useState(initSearch);
  const [statusFilter, setStatusFilter]   = useState<string[]>(initStatus);
  const [priorityFilter, setPriorityFilter] = useState<string[]>(initPriority);
  const [sourceFilter, setSourceFilter]   = useState<string[]>(initSource);
  const [workTypeFilter, setWorkTypeFilter] = useState<string[]>(initWorkType);
  const [expertiseFilter, setExpertiseFilter] = useState<string[]>(initExpertise);
  const [sortField, setSortField]         = useState<SortField>(initSortField);
  const [sortDir, setSortDir]             = useState<SortDir>(initSortDir);
  const [page, setPage]                   = useState(initPage);
  const [pageSize, setPageSize]           = useState<PageSize>(initPageSize);

  const [showCreateModal, setShowCreateModal]     = useState(false);
  const [tickets, setTickets]                     = useState<Ticket[]>([]);
  const [savingStatusIds, setSavingStatusIds]     = useState<Set<string>>(new Set());
  const [totalTickets, setTotalTickets]           = useState(0);
  const [totalPages, setTotalPages]               = useState(1);
  const [totalAllTickets, setTotalAllTickets]     = useState(0);
  const [epicCount, setEpicCount]                 = useState<number | undefined>(undefined);
  const [epicStatus, setEpicStatus]               = useState<EpicStatus | null>(null);
  const [epicRetrying, setEpicRetrying]           = useState(false);
  const [epicsRefreshKey, setEpicsRefreshKey]     = useState(0);
  const [epicsReloadSignal, setEpicsReloadSignal] = useState(0);   // bumped to refetch epic groups without remounting
  const [totalMyTickets, setTotalMyTickets]       = useState(0);
  const [isLoadingTickets, setIsLoadingTickets]   = useState(true);
  const [isSubmitting, setIsSubmitting]           = useState(false);
  const [form, setForm]                           = useState<Partial<Ticket>>(EMPTY_FORM);
  const [formError, setFormError]                 = useState('');
  const [selectedIds, setSelectedIds]             = useState<Set<string>>(new Set());
  const [selectedAllPages, setSelectedAllPages]   = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting]               = useState(false);
  const [isLoadingDetail, setIsLoadingDetail]     = useState(false);
  const [assigneeOptions, setAssigneeOptions]     = useState<AssigneeOption[]>([]);
  const [isEvaluating, setIsEvaluating]           = useState(false);
  const [isCheckingNow, setIsCheckingNow]         = useState(false);

  // Pending AI ticket-translation job (set when the user clicked
  // "Generate Tickets" on the CAB page and the job is still running).
  const [translateJob, setTranslateJob]           = useState<TranslateJobStatus | null>(null);
  const [isCheckingTranslate, setIsCheckingTranslate] = useState(false);

  // If the URL has ?ticket=<id> on initial mount, we need to gate rendering until
  // the detail fetch resolves – otherwise the list flashes briefly before the
  // detail panel takes over. Initialised synchronously so the very first render
  // already shows the loader (no flicker).
  const [isRestoringDetail, setIsRestoringDetail] = useState<boolean>(
    () => Boolean(searchParams.get('ticket'))
  );

  // Track whether we've already restored the selected ticket from the URL on mount
  const restoredTicketRef = useRef(false);

  // Debounce search input → searchQuery
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setSearchQuery(val);
      setPage(1);
    }, 300);
  };

  // ── Load assignee options from project members ───────────────────────────
  useEffect(() => {
    if (!projectId) return;
    ProjectMembersApiService.getProjectMembers(projectId, { is_deleted: false })
      .then(({ members }) => {
        const options: AssigneeOption[] = [];

        // Derive owner email: prefer prop, fall back to invited_by on any member record
        // (every member record stores the inviter's email in invited_by)
        const ownerEmailResolved = projectOwnerEmail || members[0]?.invited_by || '';

        // project_owner_name comes back on member records at runtime
        const anyMember = members[0] as ProjectMemberResponse & { project_owner_name?: string };
        const ownerLabel = projectOwnerName || anyMember?.project_owner_name || ownerEmailResolved || 'Project Owner';

        // Always add owner — even when there are no collaborators (members may be empty)
        const ownerEntry: AssigneeOption = {
          value: ownerEmailResolved || projectOwnerId || 'owner',
          label: ownerLabel,
          email: ownerEmailResolved,
          role: 'owner',
        };
        options.push(ownerEntry);

        // All invited members with active or pending invites
        members
          .filter(m =>
            m.role !== 'owner' &&
            (m.invite_status === 'accepted' || m.invite_status === 'pending')
          )
          .forEach(m => {
            options.push({
              value: m.invited_user,
              label: m.invited_user_name || m.invited_user,
              email: m.invited_user,
              role: m.role,
              pending: m.invite_status !== 'accepted',
            });
          });

        setAssigneeOptions(options);
      })
      .catch(() => {
        const fallbackEmail = projectOwnerEmail || '';
        if (fallbackEmail) {
          setAssigneeOptions([{ value: fallbackEmail, label: projectOwnerName || fallbackEmail, email: fallbackEmail, role: 'owner' }]);
        }
      });
  }, [projectId, projectOwnerId, projectOwnerName, projectOwnerEmail]);

  // ── Sync filter/sort/page state → URL ───────────────────────────────────
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      next.set('pageSize', String(pageSize));
      next.set('sortField', sortField);
      next.set('sortDir', sortDir);
      next.set('view', view);
      if (searchQuery) next.set('search', searchQuery); else next.delete('search');
      next.delete('status'); statusFilter.forEach(v => next.append('status', v));
      next.delete('priority'); priorityFilter.forEach(v => next.append('priority', v));
      next.delete('source'); sourceFilter.forEach(v => next.append('source', v));
      next.delete('workType'); workTypeFilter.forEach(v => next.append('workType', v));
      next.delete('expertise'); expertiseFilter.forEach(v => next.append('expertise', v));
      return next;
    }, { replace: true });
  }, [page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter, expertiseFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch page ───────────────────────────────────────────────────────────
  // The list is only fetched when it is actually visible (i.e. no detail
  // panel is open and we are not restoring a detail from the URL). When the
  // user lands directly on a ticket-detail URL, this avoids a wasted
  // `listTickets` call. The fetch is keyed by the current filter set so we
  // also avoid refetching on detail open/close cycles when nothing changed.
  const lastListKeyRef = useRef<string | null>(null);
  const listKey = useMemo(() => JSON.stringify({
    projectId, page, pageSize, sortField, sortDir, view, searchQuery,
    statusFilter, priorityFilter, sourceFilter, workTypeFilter, expertiseFilter,
  }), [projectId, page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter, expertiseFilter]);

  const fetchTickets = useCallback(async () => {
    if (!projectId) return;
    // The Epics view doesn't use the paginated ticket list — it loads its own
    // grouped data, so skip the ticket fetch entirely.
    if (view === 'epics') return;
    try {
      setIsLoadingTickets(true);
      const params: TicketListParams = {
        page,
        pageSize,
        sortField,
        sortDir,
        view,
        search: searchQuery || undefined,
        status: statusFilter.length ? statusFilter : undefined,
        priority: priorityFilter.length ? priorityFilter : undefined,
        source: sourceFilter.length ? sourceFilter : undefined,
        workType: workTypeFilter.length ? workTypeFilter : undefined,
        expertise: expertiseFilter.length ? expertiseFilter : undefined,
      };
      const result = await listTickets(projectId, params);
      setTickets(result.tickets);
      setTotalTickets(result.total);
      setTotalPages(result.totalPages);
      lastListKeyRef.current = listKey;
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setIsLoadingTickets(false);
    }
  }, [projectId, page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter, expertiseFilter, listKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip while the detail is open or being restored — list is hidden anyway.
    if (selectedTicket || isRestoringDetail) return;
    // Skip if we already loaded this exact filter set.
    if (lastListKeyRef.current === listKey) return;
    fetchTickets();
  }, [fetchTickets, selectedTicket, isRestoringDetail, listKey]);

  // Fetch sidebar counts via a dedicated count-only endpoint — single round
  // trip, no list rows fetched. Independent of current filters.
  const fetchCounts = useCallback(async () => {
    if (!projectId) return;
    try {
      const counts = await apiGetTicketCounts(projectId);
      setTotalAllTickets(counts.all);
      setTotalMyTickets(currentUserId ? counts.my : 0);
    } catch { /* ignore */ }
    try {
      const epics = await apiListEpics(projectId);
      setEpicCount(epics.length);
    } catch { /* ignore */ }
  }, [projectId, currentUserId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // ── Epic-generation progress ──────────────────────────────────────────────
  // Generation runs off the request path (fire-and-forget after CAB, or the
  // manual re-evaluate), so we poll a lightweight status while it's running and
  // refresh the epics view + counts the moment it finishes.
  const refreshEpicStatus = useCallback(async () => {
    if (!projectId) return null;
    try {
      const s = await apiGetEpicStatus(projectId);
      setEpicStatus(s);
      return s;
    } catch { return null; }
  }, [projectId]);

  useEffect(() => { refreshEpicStatus(); }, [refreshEpicStatus]);

  useEffect(() => {
    if (epicStatus?.status !== 'in_progress') return;
    const timer = setInterval(async () => {
      const s = await refreshEpicStatus();
      if (s && s.status !== 'in_progress') {
        fetchCounts();
        setEpicsRefreshKey(k => k + 1);   // remount Epics view with fresh data
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [epicStatus?.status, refreshEpicStatus, fetchCounts]);

  // Full (re)generation — used by both the manual "Re-evaluate" action in the
  // Epics view and the "Retry" on the failed banner. Drives the shared status
  // UX (sidebar spinner + banner) so every path looks identical.
  const regenerateEpics = async () => {
    if (!projectId || epicRetrying) return;
    setEpicRetrying(true);
    setEpicStatus({ status: 'in_progress', trigger: 'manual' });   // optimistic
    try {
      await apiGenerateEpics(projectId);
      await fetchCounts();
      setEpicsRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Epic (re)generation failed:', err);
    } finally {
      await refreshEpicStatus();
      setEpicRetrying(false);
    }
  };

  // ── Restore selected ticket from URL on mount ────────────────────────────
  // Runs in parallel with the list fetch so the detail panel appears as soon
  // as the ticket payload resolves. While in flight, render is gated by
  // `isRestoringDetail` so the user never sees the list flash before detail.
  // NOTE: the de-dupe guard via `restoredTicketRef` is intentionally placed
  // around the in-flight promise (not as an early-return) so that React 18
  // Strict Mode's double-invocation of effects does not leave the spinner
  // stuck forever (the second invocation would otherwise no-op without
  // clearing `isRestoringDetail`).
  useEffect(() => {
    if (!projectId) return;
    const ticketIdFromUrl = searchParams.get('ticket');
    if (!ticketIdFromUrl) {
      setIsRestoringDetail(false);
      return;
    }
    if (restoredTicketRef.current) return;
    restoredTicketRef.current = true;

    setIsLoadingDetail(true);
    apiGetTicket(projectId, ticketIdFromUrl)
      .then(full => setSelectedTicket(full))
      .catch(() => {
        setSearchParams(prev => {
          const n = new URLSearchParams(prev);
          n.delete('ticket');
          return n;
        }, { replace: true });
      })
      .finally(() => {
        setIsLoadingDetail(false);
        setIsRestoringDetail(false);
      });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ticket selection (with URL sync + lazy description fetch) ────────────
  const selectTicket = useCallback((ticket: Ticket | null) => {
    setSelectedTicket(ticket);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (ticket) { next.set('ticket', ticket.id); } else { next.delete('ticket'); }
      return next;
    }, { replace: true });

    if (ticket && projectId) {
      setIsLoadingDetail(true);
      apiGetTicket(projectId, ticket.id)
        .then(full => setSelectedTicket(full))
        .catch(err => console.error('Failed to load ticket detail:', err))
        .finally(() => setIsLoadingDetail(false));
    }
  }, [setSearchParams, projectId]);

  const canEdit = permissions.length === 0 || permissions.includes('update_project_permission');

  const patchForm = (patch: Partial<Ticket>) => setForm(f => ({ ...f, ...patch }));

  // Reset to page 1 whenever any filter/sort changes
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const applyFilter = (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (val: string[]) => { setter(val); setPage(1); };

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setShowCreateModal(true);
  };

  const handleCreateTicket = async () => {
    if (!form.title?.trim()) { setFormError('Title is required.'); return; }
    if (!projectId) return;
    try {
      setIsSubmitting(true);
      await apiCreateTicket(projectId, {
        title: form.title.trim(),
        description: form.description || '',
        note: form.note || undefined,
        status: (form.status as TicketStatus) || 'Open',
        priority: (form.priority as TicketPriority) || 'Medium',
        source: 'Manual',
        work_type: form.workType as TicketWorkType | undefined,
        expertise: form.expertise as TicketExpertise,
        platform_type: PLATFORM_TYPE_MAP[projectPlatformType || ''] || 'TAP',
        assign_to: form.assignTo ?? undefined,
        due_date: form.dueDate || undefined,
        cab_reference: form.cabReference || undefined,
        epic_id: form.epicId ?? undefined,
      });
      setShowCreateModal(false);
      setPage(1);
      fetchTickets();
      fetchCounts();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      setFormError('Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveTicket = async (updated: Ticket) => {
    if (!projectId) return;
    try {
      const saved = await apiUpdateTicket(projectId, updated.id, {
        title: updated.title,
        description: updated.description,
        note: updated.note ?? '',
        status: updated.status,
        priority: updated.priority,
        source: updated.source,
        work_type: updated.workType,
        expertise: updated.expertise,
        platform_type: updated.platformType,
        assign_to: updated.assignTo ?? null,
        due_date: updated.dueDate,
        cab_reference: updated.cabReference,
        epic_id: updated.epicId ?? null,
      });
      setTickets(prev => prev.map(t => t.id === saved.id ? { ...t, ...saved } : t));
      selectTicket(saved);
      fetchCounts();   // epic assignment may have created a new epic
      setEpicsReloadSignal(n => n + 1);   // keep the epics view in sync (status/epic changes)
    } catch (err) {
      console.error('Failed to update ticket:', err);
    }
  };

  // Inline status change from a list row — status-only PATCH, optimistic with
  // revert on failure. A per-ticket lock prevents overlapping saves on one row.
  const handleInlineStatusChange = async (ticket: Ticket, newStatus: TicketStatus) => {
    if (!projectId || newStatus === ticket.status || savingStatusIds.has(ticket.id)) return;
    const prevStatus = ticket.status;
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, status: newStatus } : t));
    setSavingStatusIds(prev => new Set(prev).add(ticket.id));
    try {
      const saved = await apiUpdateTicket(projectId, ticket.id, { status: newStatus });
      setTickets(prev => prev.map(t => t.id === saved.id ? { ...t, ...saved } : t));
      // With an active status filter the row may no longer belong on this
      // page — re-fetch so it drops out and the totals stay correct.
      if (statusFilter.length > 0 && !statusFilter.includes(saved.status)) {
        fetchTickets();
      }
      setEpicsReloadSignal(n => n + 1);   // epics view shows status too
    } catch (err) {
      console.error('Failed to update ticket status inline:', err);
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, status: prevStatus } : t));
    } finally {
      setSavingStatusIds(prev => { const next = new Set(prev); next.delete(ticket.id); return next; });
    }
  };

  const handleDeleteTicket = async (ticket: Ticket) => {
    if (!projectId) return;
    try {
      await apiDeleteTickets(projectId, ticket.id);
      if (selectedTicket?.id === ticket.id) selectTicket(null);
      fetchTickets();
      fetchCounts();
      setEpicsReloadSignal(n => n + 1);   // drop the ticket from the epics view too
    } catch (err) {
      console.error('Failed to delete ticket:', err);
    }
  };

  const handleEvaluateTickets = async (ticketIds: string[]) => {
    if (!projectId || ticketIds.length === 0) return;
    setIsEvaluating(true);

    // Optimistic update — show "Evaluating..." instantly before the API responds
    setTickets(prev => prev.map(t =>
      ticketIds.includes(t.id)
        ? { ...t, aiEvaluationStatus: 'pending' as TicketEvaluationStatus }
        : t
    ));
    if (selectedTicket && ticketIds.includes(selectedTicket.id)) {
      setSelectedTicket(prev => prev ? { ...prev, aiEvaluationStatus: 'pending' } : prev);
    }

    try {
      await apiEvaluateTickets(projectId, ticketIds);
    } catch (err) {
      console.error('Failed to start evaluation:', err);
      // Revert optimistic update on failure
      setTickets(prev => prev.map(t =>
        ticketIds.includes(t.id)
          ? { ...t, aiEvaluationStatus: undefined }
          : t
      ));
      if (selectedTicket && ticketIds.includes(selectedTicket.id)) {
        setSelectedTicket(prev => prev ? { ...prev, aiEvaluationStatus: undefined } : prev);
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleCheckNow = async (ticket: Ticket) => {
    if (!projectId || ticket.aiEvaluationStatus !== 'pending') return;
    setIsCheckingNow(true);
    try {
      await apiCheckEvaluationJob(projectId, ticket.id);
    } catch (err) {
      console.error('Failed to check evaluation job:', err);
    }
    try {
      const refreshed = await apiGetTicket(projectId, ticket.id);
      setSelectedTicket(refreshed);
      setTickets(prev => prev.map(t => t.id === refreshed.id ? { ...t, ...refreshed } : t));

      // Trigger navbar stats refresh if evaluation completed
      if (refreshed.aiEvaluationStatus === 'completed' && onEvaluationComplete) {
        onEvaluationComplete();
      }
    } catch (err) {
      console.error('Failed to refresh ticket after check:', err);
    } finally {
      setIsCheckingNow(false);
    }
  };

  // ── AI ticket translation: pending-job banner + polling ──────────────────
  // Fetch any pending translate job on mount / projectId change, then
  // refresh every 30 s while one is in flight. When the job clears,
  // refresh the ticket list so the newly created tickets show up.
  const refreshTranslateStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const status = await apiGetTranslateJobStatus(projectId);
      setTranslateJob(status);
    } catch {
      // ignore — keep current state
    }
  }, [projectId]);

  useEffect(() => { refreshTranslateStatus(); }, [refreshTranslateStatus]);

  useEffect(() => {
    if (!projectId || !translateJob) return;
    const timer = setInterval(async () => {
      try {
        const status = await apiGetTranslateJobStatus(projectId);
        if (!status) {
          // Job cleared — refresh tickets to surface the newly created ones,
          // and pick up epic generation which the backend has just kicked off.
          setTranslateJob(null);
          fetchTickets();
          fetchCounts();
          refreshEpicStatus();
        }
        // Still running — leave state as-is. Re-setting it here would give the
        // snapshot a new object identity every tick, which (since translateJob is
        // in this effect's deps) would tear down and rebuild the interval each
        // time. Nothing in the banner varies with the job's contents.
      } catch {
        // Silently ignore — try again on next tick
      }
    }, 10_000);
    return () => clearInterval(timer);
  }, [translateJob, projectId, fetchTickets, fetchCounts, refreshEpicStatus]);

  const handleCheckTranslate = async () => {
    if (!projectId || !translateJob) return;
    setIsCheckingTranslate(true);
    try {
      const data = await apiCheckTranslateJob(projectId);
      // External API status: pending | processing | completed | failed.
      // Only clear the banner on a true terminal state — "processing" means
      // per-ticket evaluation is still running and tickets aren't written yet.
      const isDone = data?.status === 'completed' || data?.status === 'failed';
      if (isDone) {
        setTranslateJob(null);
        fetchTickets();
        fetchCounts();
        refreshEpicStatus();
      }
    } catch {
      // 404 means the poller already resolved the job — clear banner and refresh
      setTranslateJob(null);
      fetchTickets();
      fetchCounts();
      refreshEpicStatus();
    } finally {
      setIsCheckingTranslate(false);
    }
  };

  // Silent 30-second auto-refresh while detail panel shows a pending evaluation.
  // No loading state is touched so the UI never flickers.
  useEffect(() => {
    if (!selectedTicket || !projectId) return;
    if (selectedTicket.aiEvaluationStatus !== 'pending') return;

    const ticketId = selectedTicket.id;
    const timer = setInterval(async () => {
      try {
        const refreshed = await apiGetTicket(projectId, ticketId);
        const wasCompleted = selectedTicket.aiEvaluationStatus === 'pending' && refreshed.aiEvaluationStatus === 'completed';

        setSelectedTicket(prev => {
          // Only update if the panel still shows the same ticket
          if (!prev || prev.id !== ticketId) return prev;
          return refreshed;
        });
        setTickets(prev => prev.map(t => t.id === refreshed.id ? { ...t, ...refreshed } : t));

        // Trigger navbar stats refresh if evaluation just completed
        if (wasCompleted && onEvaluationComplete) {
          onEvaluationComplete();
        }
      } catch {
        // Silently ignore refresh errors
      }
    }, 30_000);

    return () => clearInterval(timer);
  }, [selectedTicket?.id, selectedTicket?.aiEvaluationStatus, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkDelete = async () => {
    if (!projectId) return;
    setIsDeleting(true);
    try {
      let idsToDelete: string[];
      if (selectedAllPages) {
        idsToDelete = await listTicketIds(projectId, {
          sortField, sortDir, view: view === 'epics' ? 'all' : view,
          search: searchQuery || undefined,
          status: statusFilter.length ? statusFilter : undefined,
          priority: priorityFilter.length ? priorityFilter : undefined,
          source: sourceFilter.length ? sourceFilter : undefined,
          workType: workTypeFilter.length ? workTypeFilter : undefined,
          expertise: expertiseFilter.length ? expertiseFilter : undefined,
        });
      } else {
        idsToDelete = [...selectedIds];
      }
      if (idsToDelete.length === 0) return;
      await apiDeleteTickets(projectId, idsToDelete);
      if (selectedTicket && idsToDelete.includes(selectedTicket.id)) selectTicket(null);
      setSelectedIds(new Set());
      setSelectedAllPages(false);
      fetchTickets();
      fetchCounts();
    } catch (err) {
      console.error('Failed to delete tickets:', err);
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = tickets.every(t => selectedIds.has(t.id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); tickets.forEach(t => next.delete(t.id)); return next; });
      setSelectedAllPages(false);
    } else {
      setSelectedIds(prev => new Set([...prev, ...tickets.map(t => t.id)]));
      // Don't auto-set selectedAllPages — user must explicitly click the banner
    }
  };

  const selectAcrossAllPages = async () => {
    if (!projectId) return;
    setSelectedAllPages(true);
  };

  const clearAllSelection = () => {
    setSelectedIds(new Set());
    setSelectedAllPages(false);
  };

  const toggleSelectOne = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const statusOptions   = [{ value: 'Open', label: 'Open' }, { value: 'In Progress', label: 'In Progress' }, { value: 'Closed', label: 'Closed' }];
  const priorityOptions = [{ value: 'High', label: 'High' }, { value: 'Medium', label: 'Medium' }, { value: 'Low', label: 'Low' }];
  const sourceOptions   = [{ value: 'Manual', label: 'Manual' }, { value: 'CAB', label: 'CAB' }];
  const workTypeOptions = [
    { value: 'Data Model - Reference', label: 'Data Model - Reference' },
    { value: 'Data Model - Tracked', label: 'Data Model - Tracked' },
    { value: 'User Interface', label: 'User Interface' },
    { value: 'Rule', label: 'Rule' },
    { value: 'Integration', label: 'Integration' },
    { value: 'Workflow', label: 'Workflow' },
    { value: 'Report', label: 'Report' },
    { value: 'Role', label: 'Role' },
    { value: 'UI Policy', label: 'UI Policy' },
    { value: 'Other', label: 'Other' },
  ];
  const expertiseOptions = [
    { value: 'Agent', label: 'Agent' },
    { value: 'Agent + Human', label: 'Agent + Human' },
    { value: 'Human', label: 'Human' },
  ];

  const activeFilterChips: { group: string; label: string; onRemove: () => void }[] = [
    ...statusFilter.map(v => ({ group: 'Status', label: v, onRemove: () => applyFilter(setStatusFilter)(statusFilter.filter(x => x !== v)) })),
    ...priorityFilter.map(v => ({ group: 'Priority', label: v, onRemove: () => applyFilter(setPriorityFilter)(priorityFilter.filter(x => x !== v)) })),
    ...sourceFilter.map(v => ({ group: 'Source', label: v, onRemove: () => applyFilter(setSourceFilter)(sourceFilter.filter(x => x !== v)) })),
    ...workTypeFilter.map(v => ({ group: 'Work Type', label: v, onRemove: () => applyFilter(setWorkTypeFilter)(workTypeFilter.filter(x => x !== v)) })),
    ...expertiseFilter.map(v => ({ group: 'Expertise', label: v, onRemove: () => applyFilter(setExpertiseFilter)(expertiseFilter.filter(x => x !== v)) })),
  ];

  const hasActiveFilters = activeFilterChips.length > 0 || searchQuery.trim().length > 0;

  const clearAllFilters = () => {
    setStatusFilter([]); setPriorityFilter([]); setSourceFilter([]); setWorkTypeFilter([]); setExpertiseFilter([]);
    setSearchQuery(''); setSearchInput(''); setPage(1);
  };


  return (
    <>
    <div className="flex h-full gap-bg-v2-xl overflow-hidden w-full">

      {/* ── Views Panel ── */}
      <div className="w-[280px] flex-shrink-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
        <div className="flex items-center h-[60px] px-bg-v2-xl border-b border-border-secondary flex-shrink-0">
          <Text variant="text-sm" weight="semibold" color="primary">Views</Text>
        </div>
        <div className="flex-1 px-bg-v2-md pt-bg-v2-md flex flex-col gap-[2px]">
          <SidebarItem
            icon={<Icon name={IconName.Tickets} size="sm" inheritColor />}
            label="All Tickets"
            active={view === 'all'}
            count={totalAllTickets}
            onClick={() => { setView('all'); setPage(1); selectTicket(null); }}
          />
          <SidebarItem
            icon={<Icon name={IconName.Document} size="sm" inheritColor />}
            label="Epics"
            active={view === 'epics'}
            count={epicCount}
            busy={epicStatus?.status === 'in_progress'}
            onClick={() => { setView('epics'); selectTicket(null); refreshEpicStatus(); }}
          />
        </div>
      </div>

      {/* ── Main Content Panel ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden bg-bg-primary rounded-bg-v2-lg container-shadow-sm">

        {/* While restoring a ticket detail from the URL on mount, show a single
            centered loader so the list never flashes before the detail panel. */}
        {isRestoringDetail && (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-6 h-6 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
          </div>
        )}

        {/* Ticket list — hidden when detail is open or while restoring from URL */}
        <div className={`flex flex-col flex-1 min-h-0 overflow-hidden ${selectedTicket || isRestoringDetail ? 'hidden' : ''}`}>

          {/* In-progress ticket generation banner */}
          {translateJob && (
            <div className="flex items-center justify-between gap-bg-v2-md px-bg-v2-xl py-bg-v2-md border-b border-border-warning bg-bg-warning-primary flex-shrink-0">
              <div className="flex items-center gap-bg-v2-sm min-w-0">
                <div className="w-4 h-4 border-2 border-text-warning border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <Text variant="text-sm" weight="medium" color="primary">
                  Generating Tickets from CAB
                </Text>
              </div>
              <Button
                label={isCheckingTranslate ? 'Checking...' : 'Check Now'}
                variant="outline"
                size="small"
                onClick={handleCheckTranslate}
                disabled={isCheckingTranslate}
              />
            </div>
          )}

          {/* Epic generation progress — non-blocking. On the Epics view itself
              EpicsView shows its own in-context spinner/banner, so skip here. */}
          {!translateJob && view !== 'epics' && epicStatus?.status === 'in_progress' && (
            <div className="flex items-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-md border-b border-border-primary bg-bg-brand-primary-alt flex-shrink-0">
              <div className="w-4 h-4 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin flex-shrink-0" />
              <Text variant="text-sm" weight="medium" color="primary">
                Organizing tickets into Epics… You can keep working…
              </Text>
            </div>
          )}
          {!translateJob && epicStatus?.status === 'failed' && (
            <div className="flex items-center justify-between gap-bg-v2-md px-bg-v2-xl py-bg-v2-md border-b border-border-error bg-bg-error-primary flex-shrink-0">
              <Text variant="text-sm" weight="medium" color="primary">
                {epicStatus?.error === 'insufficient_credits'
                  ? 'Not enough credits to organize tickets into Epics. Please add credits and retry.'
                  : 'Couldn’t organize tickets into Epics.'}
              </Text>
              {canEdit && (
                <Button
                  label={epicRetrying ? 'Retrying…' : 'Retry'}
                  variant="outline"
                  size="small"
                  onClick={regenerateEpics}
                  disabled={epicRetrying}
                />
              )}
            </div>
          )}

          {/* Epics build-order view — replaces the toolbar + table */}
          {view === 'epics' ? (
            <EpicsView key={epicsRefreshKey} projectId={projectId || ''} canEdit={canEdit} generating={epicStatus?.status === 'in_progress'} onRegenerate={regenerateEpics} onSelectTicket={selectTicket} onCountChange={setEpicCount} onTicketStatusChange={(ticketId, status) => setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status } : t))} reloadSignal={epicsReloadSignal} focusEpicId={searchParams.get('epic')} />
          ) : (
          <>
          {/* Toolbar */}
          <div className="flex items-center h-[60px] px-bg-v2-xl border-b border-border-secondary flex-shrink-0 gap-bg-v2-md">
            {selectedIds.size > 0 && canEdit ? (
              <>
                <Text variant="text-sm" weight="semibold" color="primary" as="span">
                  {selectedAllPages ? totalTickets : selectedIds.size} selected
                </Text>
                <button
                  type="button"
                  onClick={clearAllSelection}
                  className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-brand-primary hover:underline"
                >
                  Clear
                </button>
                <div className="ml-auto flex items-center gap-bg-v2-sm">
                  {projectPlatformType !== 'Web' && (
                    <Button
                      label={
                        isEvaluating ? 'Starting...' : (
                          <span className="flex items-center gap-bg-v2-xs">
                            <Icon name={IconName.Thought} size="sm" inheritColor />
                            Re-evaluate ({selectedAllPages ? totalTickets : selectedIds.size})
                          </span>
                        )
                      }
                      variant="outline"
                      size="small"
                      onClick={() => handleEvaluateTickets([...selectedIds])}
                      disabled={isEvaluating}
                    />
                  )}
                  <Button
                    label={
                      <span className="flex items-center gap-bg-v2-xs">
                        <Icon name={IconName.Delete} size="sm" inheritColor />
                        Delete selected ({selectedAllPages ? totalTickets : selectedIds.size})
                      </span>
                    }
                    variant="outline"
                    size="small"
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
                  />
                </div>
              </>
            ) : (
              <>
                <Text variant="text-sm" weight="semibold" color="primary" as="span">
                  All Tickets
                  <Text variant="text-sm" weight="regular" color="tertiary" as="span" className="ml-bg-v2-xs">
                    ({totalTickets})
                  </Text>
                </Text>
                <div className="flex items-center gap-bg-v2-sm ml-auto">
                  <div className="w-[200px]">
                    <Input
                      placeholder="Search tickets..."
                      value={searchInput}
                      onChange={(e) => handleSearchInput(e.target.value)}
                      startIcon={<Icon name={IconName.Search} size="sm" inheritColor />}
                      className="!border !border-border-primary"
                    />
                  </div>
                  <div className="w-[140px]">
                    <MultiSelect options={statusOptions} value={statusFilter} onChange={applyFilter(setStatusFilter)} placeholder="Status" allSelectedLabel="All Status" selectAllLabel="All Status" />
                  </div>
                  <div className="w-[140px]">
                    <MultiSelect options={priorityOptions} value={priorityFilter} onChange={applyFilter(setPriorityFilter)} placeholder="Priority" allSelectedLabel="All Priority" selectAllLabel="All Priority" />
                  </div>
                  <div className="w-[130px]">
                    <MultiSelect options={sourceOptions} value={sourceFilter} onChange={applyFilter(setSourceFilter)} placeholder="Source" allSelectedLabel="All Source" selectAllLabel="All Source" />
                  </div>
                  <div className="w-[220px]">
                    <MultiSelect options={workTypeOptions} value={workTypeFilter} onChange={applyFilter(setWorkTypeFilter)} placeholder="Work Type" allSelectedLabel="All Work Types" selectAllLabel="All Work Types" />
                  </div>
                  <div className="w-[160px]">
                    <MultiSelect options={expertiseOptions} value={expertiseFilter} onChange={applyFilter(setExpertiseFilter)} placeholder="Expertise" allSelectedLabel="All Expertise" selectAllLabel="All Expertise" />
                  </div>
                  {canEdit && (
                    <Button
                      label={
                        <span className="flex items-center gap-bg-v2-xs">
                          <Icon name={IconName.Plus} size="sm" inheritColor />
                          Create Ticket
                        </span>
                      }
                      variant="primary"
                      size="small"
                      onClick={openCreateModal}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Active filters chip row */}
          {activeFilterChips.length > 0 && (
            <div className="flex items-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary bg-bg-secondary-alt flex-shrink-0 flex-wrap">
              <Text variant="text-xs" weight="semibold" color="tertiary" className="uppercase tracking-wide">
                Filters
              </Text>
              {activeFilterChips.map((chip) => (
                <span
                  key={`${chip.group}-${chip.label}`}
                  className="inline-flex items-center gap-bg-v2-xs pl-bg-v2-sm pr-bg-v2-xs py-bg-v2-xxs rounded-full bg-bg-primary border border-border-primary text-bg-v2-xs font-bg-v2-sans"
                >
                  <span className="text-text-tertiary font-bg-v2-medium">{chip.group}:</span>
                  <span className="text-text-secondary font-bg-v2-medium">{chip.label}</span>
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    aria-label={`Remove ${chip.group} ${chip.label} filter`}
                    className="flex items-center justify-center w-4 h-4 rounded-full text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-colors"
                  >
                    <Icon name={IconName.Cross} size="sm" inheritColor />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                className="ml-bg-v2-xs px-bg-v2-sm py-bg-v2-xxs text-bg-v2-xs font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline rounded-bg-v2-sm"
              >
                Clear all
              </button>
            </div>
          )}

          {/* ── Cross-page select-all banner ── */}
          {canEdit && tickets.length > 0 && tickets.every(t => selectedIds.has(t.id)) && (
            <div className="flex items-center justify-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary bg-bg-brand-primary-alt flex-shrink-0">
              {selectedAllPages ? (
                <>
                  <Text variant="text-sm" weight="medium" color="primary" as="span">
                    All <strong>{totalTickets}</strong> tickets are selected.
                  </Text>
                  <button
                    type="button"
                    onClick={clearAllSelection}
                    className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline"
                  >
                    Clear selection
                  </button>
                </>
              ) : (
                <>
                  <Text variant="text-sm" weight="medium" color="primary" as="span">
                    All <strong>{tickets.length}</strong> tickets on this page are selected.
                  </Text>
                  {totalTickets > tickets.length && (
                    <button
                      type="button"
                      onClick={selectAcrossAllPages}
                      className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline"
                    >
                      Select all {totalTickets} tickets
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Table */}
          <div className="flex-1 overflow-y-auto modern-scrollbar-spaced relative">
            {!isLoadingTickets && tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-bg-v2-md">
                <Icon name={IconName.Tickets} size="xl" inheritColor className="!text-text-tertiary" />
                <Text variant="text-sm" color="tertiary">
                  {hasActiveFilters ? 'No tickets match your filters' : 'No tickets found'}
                </Text>
                {hasActiveFilters && (
                  <Button
                    label={<span className="flex items-center gap-bg-v2-xs"><Icon name={IconName.Cross} size="sm" inheritColor />Clear all filters</span>}
                    variant="outline"
                    size="small"
                    onClick={clearAllFilters}
                  />
                )}
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-bg-secondary border-b border-border-secondary">
                    {canEdit && (
                      <th className="px-bg-v2-lg py-bg-v2-md w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={tickets.length > 0 && tickets.every(t => selectedIds.has(t.id))}
                          ref={el => {
                            if (el) el.indeterminate = tickets.some(t => selectedIds.has(t.id)) && !tickets.every(t => selectedIds.has(t.id));
                          }}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded cursor-pointer accent-bg-brand-solid"
                        />
                      </th>
                    )}
                    <ThSortable field="id" active={sortField} dir={sortDir} onSort={handleSort}>ID</ThSortable>
                    <ThSortable field="title" active={sortField} dir={sortDir} onSort={handleSort}>Title</ThSortable>
                    <ThSortable field="status" active={sortField} dir={sortDir} onSort={handleSort}>Status</ThSortable>
                    <ThSortable field="priority" active={sortField} dir={sortDir} onSort={handleSort}>Priority</ThSortable>
                    <ThSortable field="work_type" active={sortField} dir={sortDir} onSort={handleSort}>Work Type</ThSortable>
                    <ThSortable field="source" active={sortField} dir={sortDir} onSort={handleSort}>Source</ThSortable>
                    <ThSortable field="assign_to" active={sortField} dir={sortDir} onSort={handleSort}>Assigned To</ThSortable>
                    <ThSortable field="created_date" active={sortField} dir={sortDir} onSort={handleSort}>Created Date</ThSortable>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTickets ? (
                    ['w-48','w-64','w-40','w-56','w-52','w-44','w-60','w-36','w-52','w-48'].map((titleW, idx) => (
                      <tr key={idx} className="border-b border-border-secondary">
                        {canEdit && <td className="px-bg-v2-lg py-bg-v2-md w-10"><div className="w-4 h-4 skeleton-shimmer rounded" /></td>}
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-16" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className={`h-3 skeleton-shimmer rounded ${titleW}`} /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-16" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-12" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-20" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-14" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-24" /></td>
                        <td className="px-bg-v2-lg py-bg-v2-md"><div className="h-3 skeleton-shimmer rounded w-20" /></td>
                      </tr>
                    ))
                  ) : tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => selectTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                      className={`
                        border-b border-border-secondary cursor-pointer transition-colors duration-150
                        ${selectedIds.has(ticket.id) ? 'bg-bg-brand-primary-alt' : selectedTicket?.id === ticket.id ? 'bg-bg-brand-primary-alt' : 'hover:bg-bg-secondary'}
                      `}
                    >
                      {canEdit && (
                        <td className="px-bg-v2-lg py-bg-v2-md w-10" onClick={e => toggleSelectOne(ticket.id, e)}>
                          <input type="checkbox" checked={selectedIds.has(ticket.id)} onChange={() => {}} className="w-4 h-4 rounded cursor-pointer accent-bg-brand-solid" />
                        </td>
                      )}
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" weight="regular" color="secondary" as="span">{ticket.id}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md" style={{ maxWidth: 260 }} title={ticket.title}>
                        <Text variant="text-sm" weight="regular" color="primary" as="span" className="truncate block">{ticket.title}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap" onClick={canEdit ? (e) => e.stopPropagation() : undefined}>
                        {canEdit ? (
                          <InlineStatusSelect
                            status={ticket.status}
                            saving={savingStatusIds.has(ticket.id)}
                            onChange={(s) => handleInlineStatusChange(ticket, s)}
                          />
                        ) : (
                          <Text variant="text-sm" color="secondary" as="span">{ticket.status}</Text>
                        )}
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">{ticket.priority}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color={ticket.workType ? 'secondary' : 'tertiary'} as="span">{ticket.workType || '—'}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">{ticket.source}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        {ticket.assignTo
                          ? <Text variant="text-sm" color="secondary" as="span">{ticket.assignTo.name}</Text>
                          : <Text variant="text-sm" color="tertiary" as="span">—</Text>
                        }
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">
                          {new Date(ticket.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            )}
          </div>

          {/* ── Pagination bar ── */}
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalTickets}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
          </>
          )}
        </div>

        {/* Detail — full area */}
        {selectedTicket && (
          <TicketDetailPanel
            ticket={selectedTicket}
            canEdit={canEdit}
            isLoadingDetail={isLoadingDetail}
            onClose={() => selectTicket(null)}
            onSave={handleSaveTicket}
            onDelete={handleDeleteTicket}
            onEvaluate={(t) => handleEvaluateTickets([t.id])}
            onCheckNow={handleCheckNow}
            isCheckingNow={isCheckingNow}
            assigneeOptions={assigneeOptions}
          />
        )}
      </div>

    </div>

    {/* ── Bulk Delete Confirmation Modal ── */}
    <Modal
      isOpen={showBulkDeleteConfirm}
      onClose={() => setShowBulkDeleteConfirm(false)}
      title="Delete Tickets"
      size="sm"
      onSubmit={handleBulkDelete}
      submitLabel={isDeleting ? 'Deleting...' : `Delete ${selectedAllPages ? totalTickets : selectedIds.size} ticket${(selectedAllPages ? totalTickets : selectedIds.size) !== 1 ? 's' : ''}`}
      submitVariant="outline"
      submitClassName="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        Are you sure you want to delete{' '}
        <strong>{selectedAllPages ? `all ${totalTickets}` : selectedIds.size}</strong>{' '}
        ticket{(selectedAllPages ? totalTickets : selectedIds.size) !== 1 ? 's' : ''}?
        {selectedAllPages && (
          <span className="block mt-bg-v2-xs text-text-tertiary">This includes tickets across all pages.</span>
        )}
        {' '}This action cannot be undone.
      </Text>
    </Modal>

    {/* ── Create Ticket Modal ── */}
    <Modal
      isOpen={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      title="Create Ticket"
      size="lg"
      hideFooter
    >
      <TicketForm
        form={form}
        formError={formError}
        onChange={patchForm}
        onClearError={() => setFormError('')}
        assigneeOptions={assigneeOptions}
        projectId={projectId || undefined}
      />
      <ModalFooter
        submitLabel={isSubmitting ? 'Creating...' : 'Create Ticket'}
        onCancel={() => { if (!isSubmitting) setShowCreateModal(false); }}
        onSubmit={handleCreateTicket}
      />
    </Modal>

</>
  );
};

export default TicketsPage;
